<template>
  <section class="operation-page" :class="{ 'is-readonly': !canOperateCurrentModule }">
    <OperationPageHeader
      :title="config.title"
      :is-biz-page="isBizPage"
      :primary-action="primaryHeaderAction"
      :refresh-button-text="refreshButtonText"
      :fallback-action="config.action"
      :is-create-action="isCreateAction"
      @refresh="refreshCurrentPage"
      @fallback-action="handleFallbackHeaderAction"
    />

    <OrderListPanel
      v-if="isOrderCenter"
      :stats="orderStats"
      :orders="orderRows"
      :page="orderPage"
      :filters="orderFilters"
      :store-options="storeOptions"
      :show-store-filter="canViewStoreList"
      :order-status-options="orderFilterStatusOptions"
      :delivery-type-options="deliveryTypeOptions"
      :loading="loadingOrders"
      :total-pages="orderTotalPages"
      :format-amount="formatAmount"
      :get-order-status-text="getOrderStatusText"
      :get-payment-status-text="getPaymentStatusText"
      :get-delivery-type-text="getDeliveryTypeText"
      :order-status-tone="orderStatusTone"
      :payment-status-tone="paymentStatusTone"
      :tone-class="toneClass"
      :can-cancel-delivery-fee="canCancelDeliveryFee"
      @search="searchOrders"
      @reset="resetOrderFilters"
      @open-order="openOrderDetail"
      @update-status="updateOrderStatusDirect"
      @cancel-delivery-fee="requestCancelDeliveryFee"
      @change-page="changeOrderPage"
    />

    <RefundListPanel
      v-else-if="isRefundCenter"
      :stats="refundStats"
      :refunds="refundRows"
      :page="refundPage"
      :loading="loadingRefunds"
      :refreshing-refund-id="refreshingRefundId"
      :retrying-refund-id="retryingRefundId"
      :total-pages="refundTotalPages"
      :format-amount="formatAmount"
      :get-refund-status-text="getRefundStatusText"
      :get-refund-channel-text="getRefundChannelText"
      :refund-status-tone="refundStatusTone"
      :tone-class="toneClass"
      :can-retry-wechat-refund="canRetryWechatRefund"
      @open-refund="openRefundAudit"
      @refresh-wechat-status="refreshWechatStatus"
      @retry-wechat-refund="handleRetryWechatRefund"
      @change-page="changeRefundPage"
    />

    <ComplaintListPanel
      v-else-if="isComplaintCenter"
      :stats="complaintStats"
      :complaints="complaintRows"
      :page="complaintPage"
      :loading="loadingComplaints"
      :total-pages="complaintTotalPages"
      :tone-class="toneClass"
      @change-page="changeComplaintPage"
      @refresh="loadComplaints"
    />

    <DeliveryListPanel
      v-else-if="isDeliveryCenter"
      :section="isDeliveryRulesCenter ? 'rules' : (isDeliveryMinAmountCenter ? 'min-amount' : (isDeliveryAreasCenter ? 'areas' : 'points'))"
      :stats="isDeliveryRulesCenter ? deliveryRuleStats : (isDeliveryMinAmountCenter ? deliveryMinAmountStats : (isDeliveryAreasCenter ? deliveryAreaStats : deliveryPointStats))"
      :delivery-rules="deliveryRules"
      :delivery-areas="deliveryAreas"
      :delivery-points="deliveryPoints"
      :store-options="storeOptions"
      :loading-rules="loadingDeliveryRules"
      :loading-areas="loadingDeliveryAreas"
      :loading-points="loadingDeliveryPoints"
      :format-amount="formatAmount"
      :get-delivery-distance-text="getDeliveryDistanceText"
      :get-delivery-area-type-text="getDeliveryAreaTypeText"
      :get-delivery-area-value-text="getDeliveryAreaValueText"
      :delivery-area-type-tone="deliveryAreaTypeTone"
      :tone-class="toneClass"
      @edit-rule="openDeliveryRuleEditor"
      @remove-rule="removeDeliveryRule"
      @remove-area="removeDeliveryArea"
      @edit-point="openDeliveryPointEditor"
      @remove-point="removeDeliveryPoint"
      @view-point-location="openDeliveryPointLocation"
    />

    <UserListPanel
      v-else-if="isUserCenter"
      :section="isUserManagementCenter ? 'management' : (isUserBalanceCenter ? 'balance' : (isUserPointsCenter ? 'points' : 'assets'))"
      :stats="isUserManagementCenter ? userManagementStats : (isUserBalanceCenter ? userBalanceStats : (isUserPointsCenter ? userPointsStats : userAssetsStats))"
      :filters="userFilters"
      :users="userRows"
      :user-page="userPage"
      :wallet-page="userWalletLogPage"
      :points-page="userPointsLogPage"
      :loading-users="loadingUsers"
      :loading-wallet-logs="loadingUserWalletLogs"
      :loading-points-logs="loadingUserPointsLogs"
      :user-total-pages="userTotalPages"
      :format-amount="formatAmount"
      :format-signed-amount="formatSignedAmount"
      :format-signed-number="formatSignedNumber"
      :get-user-level-text="getUserLevelText"
      :get-user-status-text="getUserStatusText"
      :get-wallet-log-type-text="getWalletLogTypeText"
      :get-points-log-type-text="getPointsLogTypeText"
      :user-level-tone="userLevelTone"
      :user-status-tone="userStatusTone"
      :tone-class="toneClass"
      @search="searchUsers"
      @reset="resetUserFilters"
      @manage-user="openUserManager"
      @view-orders="viewUserOrders"
      @toggle-status="toggleUserStatus"
      @delete-user="removeUser"
      @adjust-balance="openUserBalanceEditor"
      @adjust-points="openUserPointsEditor"
      @change-user-page="changeUserPage"
      @change-asset-page="changeUserAssetPage"
    />

    <StoreListPanel
      v-else-if="isStoreCenter"
      :stats="storeStatsSummary"
      :stores="storeRows"
      :filters="storeFilters"
      :store-status-options="storeStatusOptions"
      :loading="loadingStores"
      :deleting-store-id="deletingStoreId"
      :is-platform-scope="userStore.isPlatformScope"
      :get-store-status-text="getStoreStatusText"
      :store-status-tone="storeStatusTone"
      :tone-class="toneClass"
      @search="searchStores"
      @reset="resetStoreFilters"
      @view-stats="openStoreStats"
      @edit-store="openEditStore"
      @remove-store="removeStore"
    />

    <ProductListPanel
      v-else-if="isProductCenter"
      :section="isProductList ? 'products' : (isCategoryCenter ? 'categories' : (isComboCenter ? 'combos' : (isExtraCenter ? 'extras' : 'inventory')))"
      :stats="isProductList ? productStats : (isComboCenter ? comboStats : (isInventoryCenter ? inventoryStats : []))"
      :products="productRows"
      :product-page="productPage"
      :product-filters="productFilters"
      :categories="categories"
      :store-options="storeOptions"
      :combos="combos"
      :extras="extraRows"
      :inventories="inventoryRows"
      :inventory-page="inventoryPage"
      :loading-products="loadingProducts"
      :loading-categories="loadingCategories"
      :loading-combos="loadingCombos"
      :loading-extras="loadingExtras"
      :loading-inventory="loadingInventory"
      :product-total-pages="productTotalPages"
      :inventory-total-pages="inventoryTotalPages"
      :is-platform-scope="userStore.isPlatformScope"
      :format-amount="formatAmount"
      :get-product-status-text="getProductStatusText"
      :get-category-status-text="getCategoryStatusText"
      :get-combo-status-text="getComboStatusText"
      :get-extra-status-text="getExtraStatusText"
      :get-inventory-status-text="getInventoryStatusText"
      :product-status-tone="productStatusTone"
      :category-status-tone="categoryStatusTone"
      :combo-status-tone="comboStatusTone"
      :extra-status-tone="extraStatusTone"
      :inventory-status-tone="inventoryStatusTone"
      :tone-class="toneClass"
      @search-products="searchProducts"
      @reset-products="resetProductFilters"
      @change-product-page="changeProductPage"
      @change-inventory-page="changeInventoryPage"
      @edit-product="openEditProduct"
      @edit-category="openEditCategory"
      @edit-combo="openComboEditor"
      @edit-extra="openEditExtra"
      @edit-inventory="openInventoryEditor"
      @remove-product="removeProduct"
      @remove-category="removeCategory"
      @remove-combo="removeCombo"
      @remove-extra="removeExtra"
      @manage-listing="openProductListing"
    />

    <SystemListPanel
      v-else-if="isSystemCenter"
      :section="isSystemAdminCenter ? 'admins' : (isRoleCenter ? 'roles' : (isOperationLogsCenter ? 'logs' : (isSystemParamsCenter ? 'params' : 'payments')))"
      :stats="isSystemAdminCenter ? adminStats : (isRoleCenter ? roleStats : (isOperationLogsCenter ? operationLogStats : (isSystemParamsCenter ? systemParamStats : paymentConfigStats)))"
      :admins="adminRows"
      :admin-page="adminPage"
      :store-options="storeOptions"
      :roles="systemRoles"
      :operation-log-page="operationLogPage"
      :system-params="systemParams"
      :payment-configs="paymentConfigs"
      :loading-admins="loadingAdmins"
      :loading-roles="loadingRoles"
      :loading-operation-logs="loadingOperationLogs"
      :loading-system-params="loadingSystemParams"
      :loading-payment-configs="loadingPaymentConfigs"
      :admin-total-pages="adminTotalPages"
      :operation-log-total-pages="operationLogTotalPages"
      :get-admin-status-text="getAdminStatusText"
      :get-data-scope-text="getDataScopeText"
      :get-payment-channel-text="getPaymentChannelText"
      :get-payment-config-status-text="getPaymentConfigStatusText"
      :get-payment-enabled-text="getPaymentEnabledText"
      :admin-status-tone="adminStatusTone"
      :payment-config-status-tone="paymentConfigStatusTone"
      :payment-enabled-tone="paymentEnabledTone"
      :tone-class="toneClass"
      @change-admin-page="changeAdminPage"
      @change-operation-log-page="changeOperationLogPage"
      @edit-admin="openAdminEditor"
      @edit-role="openRoleEditor"
      @edit-param="openSystemParamEditor"
      @edit-payment-config="openPaymentConfigEditor"
      @remove-admin="removeAdmin"
      @remove-role="removeRole"
      @remove-param="removeSystemParam"
    />

    <MemberListPanel
      v-else-if="isMemberCenter"
      :section="isMemberLevelsCenter ? 'levels' : (isGrowthRulesCenter ? 'growth' : (isPointsRulesCenter ? 'points' : 'benefits'))"
      :stats="memberStats"
      :member-levels="memberLevels"
      :growth-rules="growthRules"
      :points-rules="pointsRules"
      :member-benefits="memberBenefits"
      :loading="loadingMembers"
      :tone-class="toneClass"
      @save="saveMemberConfigs"
      @edit-level="openMemberLevelEditor"
      @edit-growth-rule="openGrowthRuleEditor"
      @edit-points-rule="openPointsRuleEditor"
      @edit-benefit="openMemberBenefitEditor"
      @remove-level="removeMemberLevel"
      @remove-growth-rule="removeGrowthRule"
      @remove-points-rule="removePointsRule"
      @remove-benefit="removeMemberBenefit"
    />

    <MarketingListPanel
      v-else-if="isMarketingCenter"
      :section="isCouponCenter ? 'coupons' : (isBannerCenter ? 'banners' : (isRechargeCenter ? 'recharge' : (isCardCenter ? 'cards' : (isGroupBuyCenter ? 'groupBuys' : (isBargainCenter ? 'bargains' : (isInviteCenter ? 'invites' : 'flashSales'))))))"
      :stats="isCouponCenter ? couponStats : (isBannerCenter ? bannerStats : (isRechargeCenter ? rechargeStats : (isCardCenter ? cardStats : (isGroupBuyCenter ? groupBuyStats : (isBargainCenter ? bargainStats : (isInviteCenter ? inviteStats : flashSaleStats))))))"
      :coupons="couponRows"
      :coupon-options="couponSelectOptions"
      :coupon-page="couponPage"
      :banners="banners"
      :recharge-packages="rechargePackages"
      :card-templates="cardTemplates"
      :group-buys="groupBuys"
      :bargains="bargains"
      :invite-configs="inviteConfigs"
      :flash-sales="flashSales"
      :loading-coupons="loadingCoupons"
      :loading-banners="loadingBanners"
      :loading-recharge-packages="loadingRechargePackages"
      :loading-card-templates="loadingCardTemplates"
      :loading-group-buys="loadingGroupBuys"
      :loading-bargains="loadingBargains"
      :loading-invite-configs="loadingInviteConfigs"
      :loading-flash-sales="loadingFlashSales"
      :coupon-total-pages="couponTotalPages"
      :is-platform-scope="userStore.isPlatformScope"
      :format-amount="formatAmount"
      :get-coupon-type-text="getCouponTypeText"
      :get-coupon-value-text="getCouponValueText"
      :get-coupon-status-text="getCouponStatusText"
      :get-banner-status-text="getBannerStatusText"
      :get-banner-link-text="getBannerLinkText"
      :get-recharge-status-text="getRechargeStatusText"
      :get-card-category-text="getCardCategoryText"
      :get-card-status-text="getCardStatusText"
      :get-card-value-text="getCardValueText"
      :get-group-buy-status-text="getGroupBuyStatusText"
      :get-bargain-status-text="getBargainStatusText"
      :get-invite-status-text="getInviteStatusText"
      :get-flash-sale-status-text="getFlashSaleStatusText"
      :coupon-status-tone="couponStatusTone"
      :banner-status-tone="bannerStatusTone"
      :recharge-status-tone="rechargeStatusTone"
      :card-status-tone="cardStatusTone"
      :group-buy-status-tone="groupBuyStatusTone"
      :bargain-status-tone="bargainStatusTone"
      :invite-status-tone="inviteStatusTone"
      :flash-sale-status-tone="flashSaleStatusTone"
      :tone-class="toneClass"
      @change-coupon-page="changeCouponPage"
      @edit-coupon="openCouponEditor"
      @issue-coupon="openCouponIssue"
      @remove-coupon="removeCoupon"
      @edit-banner="openBannerEditor"
      @remove-banner="removeBanner"
      @edit-recharge="openRechargePackageEditor"
      @remove-recharge="removeRechargePackage"
      @edit-card="openCardTemplateEditor"
      @remove-card="removeCardTemplate"
      @edit-group-buy="openGroupBuyEditor"
      @remove-group-buy="removeGroupBuy"
      @edit-bargain="openBargainEditor"
      @remove-bargain="removeBargain"
      @edit-invite="openInviteEditor"
      @remove-invite="removeInviteConfig"
      @edit-flash-sale="openFlashSaleEditor"
      @remove-flash-sale="removeFlashSale"
    />

    <PointsMallPanel
      v-else-if="isPointsMallCenter"
      ref="pointsMallPanelRef"
    />

    <DataListPanel
      v-else-if="isDataCenter"
      :section="isDataOverviewCenter ? 'overview' : (isDataOrdersCenter ? 'orders' : (isDataProductsCenter ? 'products' : 'stores'))"
      :stats="isDataOverviewCenter ? dataOverviewStats : (isDataOrdersCenter ? dataOrderStats : (isDataProductsCenter ? dataProductStats : storeRankStats))"
      :overview="dataOverviewData"
      :order-analysis="dataOrderAnalysis"
      :product-analysis="dataProductAnalysis"
      :store-ranks="storeRankRows"
      :loading-overview="loadingDataOverview"
      :loading-orders="loadingDataOrders"
      :loading-products="loadingDataProducts"
      :loading-store-rank="loadingStoreRank"
      :format-amount="formatAmount"
      :get-data-order-status-text="getDataOrderStatusText"
      :data-trend-tone="dataTrendTone"
      :data-order-status-tone="dataOrderStatusTone"
      :tone-class="toneClass"
    />

    <FinanceListPanel
      v-else-if="isFinanceCenter"
      :section="isTransactionsCenter ? 'transactions' : (isReconciliationCenter ? 'reconciliation' : 'refunds')"
      :stats="isTransactionsCenter ? transactionStats : (isReconciliationCenter ? reconciliationStats : refundTransactionStats)"
      :transaction-page="transactionPage"
      :reconciliation-page="reconciliationPage"
      :refund-page="refundTransactionPage"
      :loading-transactions="loadingTransactions"
      :loading-reconciliation="loadingReconciliation"
      :loading-refunds="loadingRefundTransactions"
      :transaction-total-pages="transactionTotalPages"
      :reconciliation-total-pages="reconciliationTotalPages"
      :refund-total-pages="refundTransactionTotalPages"
      :format-amount="formatAmount"
      :get-payment-channel-text="getPaymentChannelText"
      :get-finance-transaction-status-text="getFinanceTransactionStatusText"
      :get-reconciliation-status-text="getReconciliationStatusText"
      :get-reconciliation-store-name-text="getReconciliationStoreNameText"
      :get-refund-channel-text="getRefundChannelText"
      :get-refund-status-text="getRefundStatusText"
      :finance-transaction-status-tone="financeTransactionStatusTone"
      :reconciliation-status-tone="reconciliationStatusTone"
      :refund-status-tone="refundStatusTone"
      :tone-class="toneClass"
      @change-transaction-page="changeTransactionPage"
      @change-reconciliation-page="changeReconciliationPage"
      @change-refund-page="changeRefundTransactionPage"
    />

    <ProfitSharingPanel
      v-else-if="isProfitSharingModule"
      ref="profitSharingPanelRef"
      :section="isProfitSharingRulesCenter ? 'rules' : (isProfitSharingReceiversCenter ? 'receivers' : 'payments')"
    />

    <VerificationListPanel
      v-else-if="isVerificationCenter"
      :stats="verificationStats"
      :page="verificationLogPage"
      :filters="verificationFilters"
      :loading="loadingVerificationLogs"
      :total-pages="verificationLogTotalPages"
      :get-verification-result-text="getVerificationResultText"
      :verification-result-tone="verificationResultTone"
      :tone-class="toneClass"
      @search="searchVerificationLogs"
      @reset="resetVerificationFilters"
      @change-page="changeVerificationPage"
    />

    <PrinterListPanel
      v-else-if="isPrinterCenter"
      :stats="printerStats"
      :printers="printerRows"
      :loading="loadingPrinters"
      :get-printer-status-text="getPrinterStatusText"
      :printer-status-tone="printerStatusTone"
      :tone-class="toneClass"
      @edit-printer="openPrinterEditor"
      @remove-printer="removePrinter"
    />

    <MessageListPanel
      v-else-if="isMessageCenter"
      :section="isMessageLogsCenter ? 'logs' : (isMessageTemplatesCenter ? 'templates' : 'config')"
      :stats="isMessageLogsCenter ? messageLogStats : (isMessageTemplatesCenter ? messageTemplateStats : messageConfigStats)"
      :log-page="messageLogPage"
      :templates="messageTemplates"
      :configs="messageConfigRows"
      :loading-logs="loadingMessageLogs"
      :loading-templates="loadingMessageTemplates"
      :loading-config="loadingMessageConfig"
      :message-config-submitting="messageConfigSubmitting"
      :log-total-pages="messageLogTotalPages"
      :get-message-scene-text="getMessageSceneText"
      :get-message-channel-text="getMessageChannelText"
      :get-message-log-status-text="getMessageLogStatusText"
      :get-message-template-status-text="getMessageTemplateStatusText"
      :get-message-config-enabled-text="getMessageConfigEnabledText"
      :message-log-status-tone="messageLogStatusTone"
      :message-template-status-tone="messageTemplateStatusTone"
      :message-config-tone="messageConfigTone"
      :tone-class="toneClass"
      @change-log-page="changeMessageLogPage"
      @edit-template="openMessageTemplateEditor"
      @toggle-config="toggleMessageConfig"
    />

    <DroneListPanel
      v-else-if="isDroneCenter"
      :section="isDroneDevicesCenter ? 'devices' : (isDroneTasksCenter ? 'tasks' : (isDroneFlightsCenter ? 'flights' : (isDroneFaultsCenter ? 'faults' : 'maintenances')))"
      :stats="droneStats"
      :drone-page="dronePage"
      :drone-task-page="droneTaskPage"
      :drone-flight-page="droneFlightPage"
      :drone-fault-page="droneFaultPage"
      :drone-maintenance-page="droneMaintenancePage"
      :device-filters="droneDeviceFilters"
      :task-filters="droneTaskFilters"
      :flight-filters="droneFlightFilters"
      :fault-filters="droneFaultFilters"
      :maintenance-filters="droneMaintenanceFilters"
      :loading-drones="loadingDrones"
      :loading-drone-tasks="loadingDroneTasks"
      :loading-drone-flights="loadingDroneFlights"
      :loading-drone-faults="loadingDroneFaults"
      :loading-drone-maintenances="loadingDroneMaintenances"
      :deleting-drone-id="deletingDroneId"
      :total-pages="droneTotalPages"
      :format-amount="formatAmount"
      :get-drone-status-text="getDroneStatusText"
      :get-drone-task-status-text="getDroneTaskStatusText"
      :get-drone-fault-status-text="getDroneFaultStatusText"
      :get-drone-maintenance-status-text="getDroneMaintenanceStatusText"
      :get-drone-maintenance-type-text="getDroneMaintenanceTypeText"
      :drone-status-tone="droneStatusTone"
      :drone-task-status-tone="droneTaskStatusTone"
      :drone-fault-status-tone="droneFaultStatusTone"
      :drone-maintenance-status-tone="droneMaintenanceStatusTone"
      :tone-class="toneClass"
      @search="searchDrones"
      @reset="resetDroneFilters"
      @change-page="changeDronePage"
      @edit-drone="editDrone"
      @remove-drone="removeDrone"
    />

    <OperationFallbackPanel
      v-else-if="!isBizPage"
      ref="fallbackPanelRef"
      :config="config"
    />

    <OrderDialogs
      v-model:order-detail-visible="orderDetailVisible"
      v-model:refund-audit-visible="refundAuditVisible"
      v-model:verification-visible="verificationVisible"
      :selected-order="selectedOrder"
      :selected-refund="selectedRefund"
      :status-form="statusForm"
      :refund-form="refundForm"
      :verification-form="verificationForm"
      :order-status-options="orderStatusOptions"
      :status-submitting="statusSubmitting"
      :refund-submitting="refundSubmitting"
      :verification-submitting="verificationSubmitting"
      :delivery-fee-submitting="deliveryFeeSubmitting"
      :retrying-refund-id="retryingRefundId"
      v-model:delivery-fee-reason="deliveryFeeReason"
      :format-amount="formatAmount"
      :get-delivery-type-text="getDeliveryTypeText"
      :get-order-status-text="getOrderStatusText"
      :get-payment-status-text="getPaymentStatusText"
      :get-refund-status-text="getRefundStatusText"
      :get-refund-channel-text="getRefundChannelText"
      :can-audit-refund="canAuditRefund"
      :can-cancel-delivery-fee="canCancelDeliveryFee"
      :can-retry-wechat-refund="canRetryWechatRefund"
      @submit-order-status="submitOrderStatus"
      @submit-refund-audit="submitRefundAudit"
      @submit-verification="submitVerification"
      @cancel-delivery-fee="confirmCancelDeliveryFee"
      @retry-wechat-refund="confirmRetryWechatRefund"
    />

    <StoreDialogs
      v-model:store-stats-visible="storeStatsVisible"
      v-model:store-settings-visible="storeSettingsVisible"
      v-model:store-editor-visible="storeEditorVisible"
      :selected-store="selectedStore"
      :selected-store-stats="selectedStoreStats"
      :store-settings-form="storeSettingsForm"
      :store-form="storeForm"
      :store-status-options="storeStatusOptions"
      :store-settings-submitting="storeSettingsSubmitting"
      :store-submitting="storeSubmitting"
      :format-amount="formatAmount"
      @submit-store-settings="submitStoreSettings"
      @submit-store="submitStore"
    />

    <ProductDialogs
      v-model:product-editor-visible="productEditorVisible"
      v-model:combo-editor-visible="comboEditorVisible"
      v-model:category-editor-visible="categoryEditorVisible"
      v-model:extra-editor-visible="extraEditorVisible"
      v-model:inventory-editor-visible="inventoryEditorVisible"
      :product-submitting="productSubmitting"
      :combo-submitting="comboSubmitting"
      :category-submitting="categorySubmitting"
      :extra-submitting="extraSubmitting"
      :inventory-submitting="inventorySubmitting"
      :product-form="productForm"
      :combo-form="comboForm"
      :combo-item-form="comboItemForm"
      :category-form="categoryForm"
      :extra-form="extraForm"
      :inventory-form="inventoryForm"
      :product-sku-count="productSkus.length"
      v-model:combo-store-ids="comboStoreIds"
      v-model:category-store-ids="categoryStoreIds"
      v-model:inventory-store-ids="inventoryStoreIds"
      :categories="categories"
      :product-options="productOptions"
      :extra-options="extraRows"
      :store-options="storeOptions"
      @product-store-changed="handleProductStoreChange"
      @submit-product="submitProduct"
      @append-product-spec-option="appendProductSpecOption"
      @remove-product-spec-option="removeProductSpecOption"
      @set-product-topping-options="setProductToppingOptions"
      @submit-combo="submitCombo"
      @submit-category="submitCategory"
      @submit-extra="submitExtra"
      @submit-inventory="submitInventory"
      @append-combo-item="appendComboItem"
      @remove-combo-item="removeComboItem"
    />

    <ProductListingDialog
      v-model:visible="productListingVisible"
      :listing="productListing"
      :store-name="productListingStoreName"
      :loading="productListingLoading"
      :submitting="productListingSubmitting"
      @save="submitProductListing"
      @remove="removeCurrentProductListing"
    />

    <MemberDialogs
      v-model:member-level-editor-visible="memberLevelEditorVisible"
      v-model:growth-rule-editor-visible="growthRuleEditorVisible"
      v-model:points-rule-editor-visible="pointsRuleEditorVisible"
      v-model:member-benefit-editor-visible="memberBenefitEditorVisible"
      :member-level-form="memberLevelForm"
      :growth-rule-form="growthRuleForm"
      :points-rule-form="pointsRuleForm"
      :member-benefit-form="memberBenefitForm"
      :member-levels="memberLevels"
      @submit-member-level="submitMemberLevelEditor"
      @submit-growth-rule="submitGrowthRuleEditor"
      @submit-points-rule="submitPointsRuleEditor"
      @submit-member-benefit="submitMemberBenefitEditor"
    />

    <SystemDialogs
      v-model:admin-editor-visible="adminEditorVisible"
      v-model:role-editor-visible="roleEditorVisible"
      v-model:system-param-editor-visible="systemParamEditorVisible"
      v-model:payment-config-editor-visible="paymentConfigEditorVisible"
      :admin-submitting="adminSubmitting"
      :role-submitting="roleSubmitting"
      :system-params-submitting="systemParamsSubmitting"
      :payment-config-submitting="paymentConfigSubmitting"
      :admin-form="adminForm"
      :role-form="roleForm"
      :system-param-form="systemParamForm"
      :payment-config-form="paymentConfigForm"
      :system-roles="systemRoles"
      :store-options="storeOptions"
      @admin-role-ids-touched="adminRoleIdsTouched = true"
      @submit-admin="submitAdmin"
      @submit-role="submitRole"
      @submit-system-param="submitSystemParam"
      @submit-payment-config="submitPaymentConfig"
    />

    <MarketingDialogs
      v-model:coupon-editor-visible="couponEditorVisible"
      v-model:coupon-issue-visible="couponIssueVisible"
      v-model:banner-editor-visible="bannerEditorVisible"
      v-model:recharge-package-editor-visible="rechargePackageEditorVisible"
      v-model:card-template-editor-visible="cardTemplateEditorVisible"
      v-model:group-buy-editor-visible="groupBuyEditorVisible"
      v-model:bargain-editor-visible="bargainEditorVisible"
      v-model:invite-editor-visible="inviteEditorVisible"
      v-model:flash-sale-editor-visible="flashSaleEditorVisible"
      :coupon-submitting="couponSubmitting"
      :banner-submitting="bannerSubmitting"
      :recharge-submitting="rechargeSubmitting"
      :card-submitting="cardSubmitting"
      :group-buy-submitting="groupBuySubmitting"
      :bargain-submitting="bargainSubmitting"
      :invite-submitting="inviteSubmitting"
      :flash-submitting="flashSubmitting"
      :coupon-form="couponForm"
      :coupon-issue-form="couponIssueForm"
      :banner-form="bannerForm"
      :recharge-package-form="rechargePackageForm"
      :is-editing-recharge-package="isEditingRechargePackage"
      :card-template-form="cardTemplateForm"
      :group-buy-form="groupBuyForm"
      :bargain-form="bargainForm"
      :invite-form="inviteForm"
      :flash-sale-form="flashSaleForm"
      :flash-sale-item-form="flashSaleItemForm"
      v-model:coupon-store-ids="couponStoreIds"
      v-model:banner-store-id="bannerStoreId"
      v-model:card-template-store-ids="cardTemplateStoreIds"
      v-model:group-buy-store-ids="groupBuyStoreIds"
      v-model:bargain-store-ids="bargainStoreIds"
      v-model:flash-sale-store-ids="flashSaleStoreIds"
      v-model:coupon-issue-target-user-ids="couponIssueTargetUserIds"
      v-model:recharge-package-gift-coupon-template-ids="rechargePackageGiftCouponTemplateIds"
      :store-options="storeOptions"
      :product-options="productOptions"
      :user-options="userOptions"
      :coupon-options="couponSelectOptions"
      :format-amount="formatAmount"
      @submit-coupon="submitCoupon"
      @submit-coupon-issue="submitCouponIssue"
      @submit-banner="submitBanner"
      @submit-recharge-package="submitRechargePackage"
      @submit-card-template="submitCardTemplate"
      @submit-group-buy="submitGroupBuy"
      @submit-bargain="submitBargain"
      @submit-invite="submitInvite"
      @submit-flash-sale="submitFlashSale"
      @append-flash-sale-item="appendFlashSaleItem"
      @remove-flash-sale-item="removeFlashSaleItem"
    />

    <MessageDialogs
      v-model:printer-editor-visible="printerEditorVisible"
      v-model:message-template-editor-visible="messageTemplateEditorVisible"
      :printer-submitting="printerSubmitting"
      :message-template-submitting="messageTemplateSubmitting"
      :printer-form="printerForm"
      :message-template-form="messageTemplateForm"
      v-model:printer-store-id="printerStoreId"
      :store-options="storeOptions"
      @submit-printer="submitPrinter"
      @submit-message-template="submitMessageTemplate"
    />

    <DeliveryDialogs
      v-model:delivery-rule-editor-visible="deliveryRuleEditorVisible"
      v-model:delivery-area-editor-visible="deliveryAreaEditorVisible"
      v-model:delivery-point-editor-visible="deliveryPointEditorVisible"
      v-model:delivery-point-location-visible="deliveryPointLocationVisible"
      :delivery-rule-submitting="deliveryRuleSubmitting"
      :delivery-area-submitting="deliveryAreaSubmitting"
      :delivery-point-submitting="deliveryPointSubmitting"
      :delivery-rule-form="deliveryRuleForm"
      :delivery-area-form="deliveryAreaForm"
      :delivery-point-form="deliveryPointForm"
      :selected-delivery-point="selectedDeliveryPoint"
      v-model:delivery-rule-store-ids="deliveryRuleStoreIds"
      v-model:delivery-area-store-ids="deliveryAreaStoreIds"
      :store-options="storeOptions"
      @submit-delivery-rule="submitDeliveryRule"
      @submit-delivery-area="submitDeliveryArea"
      @submit-delivery-point="submitDeliveryPoint"
    />

    <UserDialogs
      v-model:user-manager-visible="userManagerVisible"
      v-model:user-balance-editor-visible="userBalanceEditorVisible"
      v-model:user-points-editor-visible="userPointsEditorVisible"
      v-model:member-editor-visible="memberEditorVisible"
      :user-balance-submitting="userBalanceSubmitting"
      :user-points-submitting="userPointsSubmitting"
      :member-profile-submitting="memberProfileSubmitting"
      :user-admin-permission-submitting="userAdminPermissionSubmitting"
      :user-balance-form="userBalanceForm"
      :user-points-form="userPointsForm"
      :member-form="memberForm"
      :member-levels="memberLevels"
      :selected-consumer-user="selectedConsumerUser"
      :selected-member-profile="selectedMemberProfile"
      :wallet-page="userWalletLogPage"
      :points-page="userPointsLogPage"
      :loading-wallet-logs="loadingUserWalletLogs"
      :loading-points-logs="loadingUserPointsLogs"
      :loading-member-profile="loadingMemberProfile"
      :can-view-member="canViewMember"
      :can-edit-member="canEditMember"
      :can-edit-user-admin-permission="canEditUserAdminPermission"
      :format-amount="formatAmount"
      :format-signed-amount="formatSignedAmount"
      :format-signed-number="formatSignedNumber"
      :get-user-level-text="getUserLevelText"
      :get-user-status-text="getUserStatusText"
      :get-wallet-log-type-text="getWalletLogTypeText"
      :get-points-log-type-text="getPointsLogTypeText"
      :has-user-admin-order-permission="hasUserAdminOrderPermission"
      @open-user-balance="openUserBalanceEditor"
      @open-user-points="openUserPointsEditor"
      @open-member-editor="openMemberEditor"
      @remove-member-profile="removeMemberProfile"
      @submit-user-balance="submitUserBalance"
      @submit-user-points="submitUserPoints"
      @submit-member-profile="submitMemberProfile"
      @submit-user-admin-permission="submitUserAdminPermission"
    />

    <el-dialog v-model="droneEditorVisible" :title="droneForm.id ? '编辑设备' : '新增设备'" width="760px">
      <div class="detail-grid detail-grid--single">
        <article class="detail-card">
          <div class="detail-actions">
            <GuidedField label="设备编号" required>
              <el-input v-model.trim="droneForm.droneNo" placeholder="如 DRONE-GX-001" />
            </GuidedField>
            <GuidedField label="设备名称">
              <el-input v-model.trim="droneForm.name" placeholder="如 高新一号机" />
            </GuidedField>
            <GuidedField label="设备型号">
              <el-input v-model.trim="droneForm.model" placeholder="如 LALP-X1" />
            </GuidedField>
            <GuidedField label="设备类型">
              <el-input v-model.trim="droneForm.deviceType" placeholder="如 DELIVERY" />
            </GuidedField>
            <GuidedField label="仓库编码">
              <el-input v-model.trim="droneForm.warehouseCode" placeholder="如 WH-GX-001" />
            </GuidedField>
            <GuidedField label="所属门店">
              <el-select v-model="droneStoreIds" multiple filterable placeholder="请选择门店">
                <el-option v-for="store in storeOptions" :key="store.id" :label="store.name" :value="store.id" />
              </el-select>
            </GuidedField>
            <GuidedField label="电量">
              <el-input-number v-model="droneForm.battery" :min="0" :max="100" controls-position="right" />
            </GuidedField>
            <GuidedField label="设备状态">
              <el-select v-model="droneForm.status">
                <el-option label="空闲" value="IDLE" />
                <el-option label="充电中" value="CHARGING" />
                <el-option label="维护中" value="MAINTENANCE" />
                <el-option label="故障" value="FAULT" />
                <el-option label="离线" value="OFFLINE" />
              </el-select>
            </GuidedField>
            <GuidedField label="备注" wide>
              <el-input v-model.trim="droneForm.remark" type="textarea" :rows="3" placeholder="请输入备注" />
            </GuidedField>
          </div>
        </article>
      </div>
      <template #footer>
        <el-button @click="droneEditorVisible = false">取消</el-button>
        <el-button type="primary" :loading="droneSubmitting" :disabled="!droneForm.droneNo" @click="submitDrone">{{ droneForm.id ? '保存修改' : '创建设备' }}</el-button>
      </template>
    </el-dialog>
  </section>
</template>

<script setup lang="ts">
import { computed, reactive, ref, watch, type Ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { ElMessage, ElMessageBox } from 'element-plus'
import { DeliveryType, getAllowedOrderStatusTransitions, OrderStatus, ORDER_STATUS_LABELS, PaymentStatus, RefundStatus } from '@jiaofa/constants'
import { fenToYuan, yuanToFen } from '@jiaofa/shared-utils'
import DataListPanel from '../data/components/DataListPanel.vue'
import { useDataCenter } from '../data/composables/useDataCenter'
import DeliveryListPanel from '../delivery/components/DeliveryListPanel.vue'
import { useDeliveryCenter } from '../delivery/composables/useDeliveryCenter'
import DeliveryDialogs from '../delivery/dialogs/DeliveryDialogs.vue'
import DroneListPanel from '../drones/components/DroneListPanel.vue'
import FinanceListPanel from '../finance/components/FinanceListPanel.vue'
import ProfitSharingPanel from '../finance/components/ProfitSharingPanel.vue'
import GuidedField from '@/components/GuidedField.vue'
import { useFinanceCenter } from '../finance/composables/useFinanceCenter'
import MarketingListPanel from '../marketing/components/MarketingListPanel.vue'
import MarketingDialogs from '../marketing/dialogs/MarketingDialogs.vue'
import MemberListPanel from '../members/components/MemberListPanel.vue'
import MemberDialogs from '../members/dialogs/MemberDialogs.vue'
import OrderDialogs from '../orders/dialogs/OrderDialogs.vue'
import OrderListPanel from '../orders/components/OrderListPanel.vue'
import PointsMallPanel from '../points-mall/components/PointsMallPanel.vue'
import ProductListPanel from '../products/components/ProductListPanel.vue'
import ProductDialogs from '../products/dialogs/ProductDialogs.vue'
import ProductListingDialog from '../products/dialogs/ProductListingDialog.vue'
import PrinterListPanel from '../printer/components/PrinterListPanel.vue'
import { usePrinterCenter } from '../printer/composables/usePrinterCenter'
import RefundListPanel from '../orders/components/RefundListPanel.vue'
import ComplaintListPanel from '../orders/components/ComplaintListPanel.vue'
import StoreListPanel from '../stores/components/StoreListPanel.vue'
import StoreDialogs from '../stores/dialogs/StoreDialogs.vue'
import MessageListPanel from '../system/components/MessageListPanel.vue'
import MessageDialogs from '../system/dialogs/MessageDialogs.vue'
import { useMessageCenter } from '../system/composables/useMessageCenter'
import SystemListPanel from '../system/components/SystemListPanel.vue'
import SystemDialogs from '../system/dialogs/SystemDialogs.vue'
import UserListPanel from '../users/components/UserListPanel.vue'
import { useUserCenter } from '../users/composables/useUserCenter'
import UserDialogs from '../users/dialogs/UserDialogs.vue'
import VerificationListPanel from '../verification/components/VerificationListPanel.vue'
import { useVerificationLogs } from '../verification/composables/useVerificationLogs'
import OperationFallbackPanel from './components/OperationFallbackPanel.vue'
import OperationPageHeader from './components/OperationPageHeader.vue'
import { useOperationPageConfig } from './composables/useOperationPageConfig'
import { useOperationPageFlags } from './composables/useOperationPageFlags'
import { useUserStore } from '@/stores/user'
import { expandImplicitPermissions } from '@/utils/permissions'
import { splitStockAcrossSkus } from '@/utils/stock-split'
import { echoSpecGroupsForEditor, normalizeSpecGroupsForSave } from '@/utils/product-spec-groups'
import { MenuTab, PagePermission, Permission } from '@jiaofa/constants'
import type { OperationStat } from '@/data/operationDesign'
import { adminStatusTone, bargainStatusTone, canAuditRefund, cardStatusTone, categoryStatusTone, comboStatusTone, couponStatusTone, dataOrderStatusTone, dataTrendTone, deliveryAreaTypeTone, extraStatusTone, financeTransactionStatusTone, flashSaleStatusTone, formatAmount, formatSignedAmount, formatSignedNumber, getAdminStatusText, getBargainStatusText, getCardCategoryText, getCardStatusText, getCardValueText, getCategoryStatusText, getComboStatusText, getCouponStatusText, getCouponTypeText, getCouponValueText, getDataOrderStatusText, getDataScopeText, getDeliveryAreaTypeText, getDeliveryAreaValueText, getDeliveryDistanceText, getDeliveryTypeText, getExtraStatusText, getFinanceTransactionStatusText, getFlashSaleStatusText, getGroupBuyStatusText, getInventoryStatusText, getInviteStatusText, getMessageChannelText, getMessageConfigEnabledText, getMessageLogStatusText, getMessageSceneText, getMessageTemplateStatusText, getOrderStatusText, getPaymentChannelText, getPaymentConfigStatusText, getPaymentEnabledText, getPaymentStatusText, getPointsLogTypeText, getPrinterStatusText, getProductStatusText, getRechargeStatusText, getReconciliationStatusText, getReconciliationStoreNameText, getRefundChannelText, getRefundStatusText, getStoreStatusText, getUserLevelText, getUserStatusText, getVerificationResultText, getWalletLogTypeText, groupBuyStatusTone, inventoryStatusTone, inviteStatusTone, messageConfigTone, messageLogStatusTone, messageTemplateStatusTone, orderStatusTone, paymentConfigStatusTone, paymentEnabledTone, paymentStatusTone, printerStatusTone, productStatusTone, rechargeStatusTone, reconciliationStatusTone, refundStatusTone, storeStatusTone, userLevelTone, userStatusTone, verificationResultTone } from './utils/operationFormatters'
import { createDrone, deleteDrone, fetchDroneFlights, fetchDroneFaults, fetchDroneMaintenances, fetchDroneOverview, fetchDrones, fetchDroneTasks, updateDrone, type Drone, type DroneFault, type DroneFaultListParams, type DroneFlightListParams, type DroneListParams, type DroneMaintenance, type DroneMaintenanceListParams, type DroneOverview, type DroneSavePayload, type DroneTask, type DroneTaskListParams } from '@/api/drone'
import { createBanner, createBargain, createCardTemplate, createCoupon, createFlashSale, createGroupBuy, createInviteConfig, createRechargePackage, deleteBanner, deleteBargain, deleteCardTemplate, deleteCoupon, deleteFlashSale, deleteGroupBuy, deleteInviteConfig, deleteRechargePackage, fetchBanners, fetchBargains, fetchCardTemplates, fetchCoupons, fetchFlashSales, fetchGroupBuys, fetchInviteConfigs, fetchRechargePackages, issueCoupon, updateBanner, updateBargain, updateCardTemplate, updateCoupon, updateFlashSale, updateGroupBuy, updateInviteConfig, updateRechargePackage, type AdminBanner, type AdminBargainActivity, type AdminCardTemplate, type AdminCoupon, type AdminFlashSale, type AdminGroupBuy, type AdminInviteConfig, type AdminRechargePackage, type BannerSavePayload, type BargainSavePayload, type CardTemplateSavePayload, type CouponIssuePayload, type CouponSavePayload, type FlashSaleItem, type FlashSaleSavePayload, type GroupBuySavePayload, type InviteConfigSavePayload, type RechargePackageSavePayload } from '@/api/marketing'
import { createMemberLevel, deleteMemberLevel, fetchGrowthRules, fetchMemberBenefits, fetchMemberLevels, fetchPointsRules, updateGrowthRules, updateMemberBenefits, updateMemberLevel, updateMemberLevels, updatePointsRules, type GrowthRuleConfig, type MemberBenefitConfig, type MemberLevelConfig, type MemberLevelSavePayload, type PointsRuleConfig } from '@/api/member'
import { auditRefund, cancelDeliveryFee, fetchOrderDetail, fetchOrders, fetchRefunds, refreshWechatRefundStatus, retryWechatRefund, updateOrderStatus, type AdminOrder, type AdminRefund, type DeliveryFeeCancelResult } from '@/api/order'
import { fetchComplaints } from '@/api/complaint'
import { createCategory, createCombo, createExtra, createProduct, deleteCategory, deleteCombo, deleteExtra, deleteProduct, fetchCategories, fetchCombos, fetchExtras, fetchInventory, fetchProductListing, fetchProducts, fetchSkus, removeProductListing, saveProductListing, updateCategory, updateCombo, updateExtra, updateInventory, updateProduct, type AdminCategory, type AdminCombo, type AdminComboItem, type AdminExtra, type AdminInventory, type AdminProduct, type AdminSku, type CategorySavePayload, type ComboSavePayload, type ExtraSavePayload, type InventoryListParams, type InventoryUpdatePayload, type ProductListing, type ProductListingSavePayload, type ProductListParams, type ProductSavePayload, type ProductSpecGroup } from '@/api/product'
import { createStore, deleteStore, fetchStores, fetchStoreStats, updateStore, updateStoreSettings, type AdminStore, type StoreSavePayload, type StoreStats } from '@/api/store'
import { createAdmin, createRole, deleteAdmin, deleteRole, deleteSystemParam, fetchAdmins, fetchOperationLogs, fetchPaymentConfigs, fetchRoles, fetchSystemParams, updateAdmin, updatePaymentConfig, updateRole, updateSystemParams, type AdminAccount, type AdminRole, type AdminSavePayload, type PaymentConfig, type PaymentConfigSavePayload, type RoleSavePayload, type SystemParam, type SystemParamSavePayload } from '@/api/system'
import { fetchAdminUsers, type AdminConsumerUser } from '@/api/user'

type HeaderAction = {
  label: string
  icon: 'plus' | 'edit'
  handler: () => void
}

type GrowthRuleEditorForm = GrowthRuleConfig & { growthValuePerYuan?: number }

function fenAmountToYuan(amountInFen?: number): number | undefined {
  return amountInFen === undefined ? undefined : Number(fenToYuan(amountInFen))
}

function yuanAmountToFen(amountInYuan?: number | string | null): number | undefined {
  return amountInYuan === undefined || amountInYuan === null || amountInYuan === '' ? undefined : yuanToFen(amountInYuan)
}

const route = useRoute()
const router = useRouter()
const userStore = useUserStore()
const canViewStoreList = computed(() => userStore.hasEveryPermission([PagePermission.StoreList.view]))
const designKey = computed(() => String(route.meta.designKey ?? ''))
const pageTitle = computed(() => String(route.meta.title ?? '运营模块'))
const fallbackPanelRef = ref<InstanceType<typeof OperationFallbackPanel>>()
const pointsMallPanelRef = ref<InstanceType<typeof PointsMallPanel>>()
const profitSharingPanelRef = ref<InstanceType<typeof ProfitSharingPanel>>()
const {
  isOrderCenter,
  isRefundCenter,
  isComplaintCenter,
  isStoreCenter,
  isProductList,
  isCategoryCenter,
  isComboCenter,
  isExtraCenter,
  isInventoryCenter,
  isProductCenter,
  isDeliveryRulesCenter,
  isDeliveryMinAmountCenter,
  isDeliveryAreasCenter,
  isDeliveryPointsCenter,
  isDeliveryCenter,
  isDroneDevicesCenter,
  isDroneTasksCenter,
  isDroneFlightsCenter,
  isDroneFaultsCenter,
  isDroneMaintenancesCenter,
  isDroneCenter,
  isUserManagementCenter,
  isUserBalanceCenter,
  isUserPointsCenter,
  isUserAssetsCenter,
  isUserCenter,
  isMemberLevelsCenter,
  isGrowthRulesCenter,
  isPointsRulesCenter,
  isMemberBenefitsCenter,
  isMemberRulesCenter,
  isMemberCenter,
  isSystemAdminCenter,
  isRoleCenter,
  isOperationLogsCenter,
  isSystemParamsCenter,
  isPaymentConfigCenter,
  isSystemCenter,
  isVerificationCenter,
  isPrinterCenter,
  isMessageLogsCenter,
  isMessageTemplatesCenter,
  isMessageConfigCenter,
  isMessageCenter,
  isDataOverviewCenter,
  isDataOrdersCenter,
  isDataProductsCenter,
  isStoreRankCenter,
  isDataCenter,
  isTransactionsCenter,
  isReconciliationCenter,
  isRefundTransactionsCenter,
  isProfitSharingCenter,
  isProfitSharingRulesCenter,
  isProfitSharingReceiversCenter,
  isProfitSharingModule,
  isFinanceCenter,
  isCouponCenter,
  isPointsMallCenter,
  isCardCenter,
  isRechargeCenter,
  isBannerCenter,
  isGroupBuyCenter,
  isBargainCenter,
  isInviteCenter,
  isFlashSaleCenter,
  isMarketingCenter,
  isBizPage,
} = useOperationPageFlags(designKey)
const moduleEditPermission = computed(() => {
  if (isOrderCenter.value) return PagePermission.OrderCenter.edit
  if (isRefundCenter.value) return PagePermission.OrderRefunds.edit
  if (isComplaintCenter.value) return PagePermission.OrderComplaints.edit
  if (isStoreCenter.value) return PagePermission.StoreList.edit
  if (isPrinterCenter.value) return PagePermission.StorePrinters.edit
  if (isProductList.value || isInventoryCenter.value) return PagePermission.ProductList.edit
  if (isCategoryCenter.value) return PagePermission.ProductCategories.edit
  if (isComboCenter.value) return PagePermission.ProductCombos.edit
  if (isExtraCenter.value) return PagePermission.ProductExtras.edit
  if (isDeliveryRulesCenter.value || isDeliveryMinAmountCenter.value) return PagePermission.DeliveryMinAmount.edit
  if (isDeliveryAreasCenter.value) return PagePermission.DeliveryAreas.edit
  if (isDeliveryPointsCenter.value) return PagePermission.DeliveryPoints.edit
  if (isDroneDevicesCenter.value) return PagePermission.DroneDevices.edit
  if (isDroneTasksCenter.value) return PagePermission.DroneTasks.edit
  if (isDroneFlightsCenter.value) return PagePermission.DroneFlights.edit
  if (isDroneFaultsCenter.value) return PagePermission.DroneFaults.edit
  if (isDroneMaintenancesCenter.value) return PagePermission.DroneMaintenances.edit
  if (isUserCenter.value) return PagePermission.UserList.edit
  if (isMemberLevelsCenter.value) return PagePermission.MemberLevels.edit
  if (isMemberRulesCenter.value || isGrowthRulesCenter.value || isPointsRulesCenter.value) return PagePermission.MemberRules.edit
  if (isMemberBenefitsCenter.value) return PagePermission.MemberBenefits.edit
  if (isCouponCenter.value) return PagePermission.MarketingCoupons.edit
  if (isPointsMallCenter.value) return PagePermission.MarketingPointsMall.edit
  if (isCardCenter.value) return PagePermission.MarketingCards.edit
  if (isRechargeCenter.value) return PagePermission.MarketingRecharge.edit
  if (isBannerCenter.value) return PagePermission.MarketingBanners.edit
  if (isGroupBuyCenter.value) return PagePermission.MarketingGroupBuy.edit
  if (isBargainCenter.value) return PagePermission.MarketingBargain.edit
  if (isInviteCenter.value) return PagePermission.MarketingInvite.edit
  if (isFlashSaleCenter.value) return PagePermission.MarketingFlashSale.edit
  if (isDataOverviewCenter.value) return PagePermission.DataOverview.edit
  if (isDataOrdersCenter.value) return PagePermission.DataOrders.edit
  if (isDataProductsCenter.value) return PagePermission.DataProducts.edit
  if (isStoreRankCenter.value) return PagePermission.DataStoreRank.edit
  if (isTransactionsCenter.value) return PagePermission.FinanceTransactions.edit
  if (isReconciliationCenter.value) return PagePermission.FinanceReconciliation.edit
  if (isRefundTransactionsCenter.value) return PagePermission.FinanceRefunds.edit
  if (isProfitSharingCenter.value) return PagePermission.FinanceProfitSharing.edit
  if (isProfitSharingRulesCenter.value) return PagePermission.FinanceProfitSharingRules.edit
  if (isProfitSharingReceiversCenter.value) return PagePermission.FinanceProfitSharingReceivers.edit
  if (isMessageCenter.value) return PagePermission.SystemMessages.edit
  if (isRoleCenter.value) return PagePermission.SystemRoles.edit
  if (isSystemAdminCenter.value) return PagePermission.SystemAdmins.edit
  if (isSystemParamsCenter.value || isPaymentConfigCenter.value) return PagePermission.SystemParams.edit
  return undefined
})
const canOperateCurrentModule = computed(() =>
  !moduleEditPermission.value || userStore.hasEveryPermission([moduleEditPermission.value]),
)
const canViewMember = computed(() => userStore.hasEveryPermission([Permission.MemberView]))
const canEditMember = computed(() => userStore.hasEveryPermission([Permission.MemberEdit]))
const canEditUserAdminPermission = computed(() => userStore.hasEveryPermission([Permission.UserEdit]))

const loadingOrders = ref(false)
const loadingRefunds = ref(false)
const loadingComplaints = ref(false)
const loadingStores = ref(false)
const loadingProducts = ref(false)
const loadingCategories = ref(false)
const loadingCombos = ref(false)
const loadingExtras = ref(false)
const loadingInventory = ref(false)
const loadingMembers = ref(false)
const loadingAdmins = ref(false)
const loadingRoles = ref(false)
const loadingOperationLogs = ref(false)
const loadingSystemParams = ref(false)
const loadingPaymentConfigs = ref(false)
const loadingCoupons = ref(false)
const loadingBanners = ref(false)
const loadingRechargePackages = ref(false)
const loadingCardTemplates = ref(false)
const loadingGroupBuys = ref(false)
const loadingBargains = ref(false)
const loadingInviteConfigs = ref(false)
const loadingFlashSales = ref(false)
const loadingDrones = ref(false)
const loadingDroneTasks = ref(false)
const loadingDroneFlights = ref(false)
const loadingDroneFaults = ref(false)
const loadingDroneMaintenances = ref(false)
const deletingDroneId = ref('')
const orderPage = ref<Awaited<ReturnType<typeof fetchOrders>>>()
const adminPage = ref<Awaited<ReturnType<typeof fetchAdmins>>>()
const refundPage = ref<Awaited<ReturnType<typeof fetchRefunds>>>()
const complaintPage = ref<Awaited<ReturnType<typeof fetchComplaints>>>()
const operationLogPage = ref<Awaited<ReturnType<typeof fetchOperationLogs>>>()
const productPage = ref<Awaited<ReturnType<typeof fetchProducts>>>()
const inventoryPage = ref<Awaited<ReturnType<typeof fetchInventory>>>()
const couponPage = ref<Awaited<ReturnType<typeof fetchCoupons>>>()
const droneOverview = ref<DroneOverview>()
const dronePage = ref<Awaited<ReturnType<typeof fetchDrones>>>()
const droneTaskPage = ref<Awaited<ReturnType<typeof fetchDroneTasks>>>()
const droneFlightPage = ref<Awaited<ReturnType<typeof fetchDroneFlights>>>()
const droneFaultPage = ref<Awaited<ReturnType<typeof fetchDroneFaults>>>()
const droneMaintenancePage = ref<Awaited<ReturnType<typeof fetchDroneMaintenances>>>()
const storeRows = ref<AdminStore[]>([])
const storeOptions = ref<AdminStore[]>([])
const userOptions = ref<AdminConsumerUser[]>([])
const categories = ref<AdminCategory[]>([])
const combos = ref<AdminCombo[]>([])
const productOptions = ref<AdminProduct[]>([])
const extraRows = ref<AdminExtra[]>([])
const inventoryRows = ref<AdminInventory[]>([])
const memberLevels = ref<MemberLevelConfig[]>([])
const growthRules = ref<GrowthRuleConfig[]>([])
const pointsRules = ref<PointsRuleConfig[]>([])
const memberBenefits = ref<MemberBenefitConfig[]>([])
const adminRows = ref<AdminAccount[]>([])
const systemRoles = ref<AdminRole[]>([])
const systemParams = ref<SystemParam[]>([])
const paymentConfigs = ref<PaymentConfig[]>([])
const rechargePackages = ref<AdminRechargePackage[]>([])
const banners = ref<AdminBanner[]>([])
const cardTemplates = ref<AdminCardTemplate[]>([])
const groupBuys = ref<AdminGroupBuy[]>([])
const bargains = ref<AdminBargainActivity[]>([])
const inviteConfigs = ref<AdminInviteConfig[]>([])
const flashSales = ref<AdminFlashSale[]>([])
const couponOptions = ref<AdminCoupon[]>([])

const orderDetailVisible = ref(false)
const refundAuditVisible = ref(false)
const storeStatsVisible = ref(false)
const storeSettingsVisible = ref(false)
const storeEditorVisible = ref(false)
const productEditorVisible = ref(false)
const categoryEditorVisible = ref(false)
const comboEditorVisible = ref(false)
const extraEditorVisible = ref(false)
const inventoryEditorVisible = ref(false)
const memberLevelEditorVisible = ref(false)
const growthRuleEditorVisible = ref(false)
const pointsRuleEditorVisible = ref(false)
const memberBenefitEditorVisible = ref(false)
const adminEditorVisible = ref(false)
const roleEditorVisible = ref(false)
const systemParamEditorVisible = ref(false)
const paymentConfigEditorVisible = ref(false)
const couponEditorVisible = ref(false)
const couponIssueVisible = ref(false)
const bannerEditorVisible = ref(false)
const rechargePackageEditorVisible = ref(false)
const rechargePackageEditing = ref(false)
const cardTemplateEditorVisible = ref(false)
const groupBuyEditorVisible = ref(false)
const bargainEditorVisible = ref(false)
const inviteEditorVisible = ref(false)
const flashSaleEditorVisible = ref(false)
const droneEditorVisible = ref(false)
const selectedOrder = ref<AdminOrder>()
const selectedRefund = ref<AdminRefund>()
const complaintRows = computed(() => complaintPage.value?.list ?? [])
const selectedStore = ref<AdminStore>()
const selectedStoreStats = ref<StoreStats>()

const statusSubmitting = ref(false)
const refundSubmitting = ref(false)
const refreshingRefundId = ref('')
const retryingRefundId = ref('')
const deliveryFeeSubmitting = ref(false)
const storeSettingsSubmitting = ref(false)
const storeSubmitting = ref(false)
const productSubmitting = ref(false)
const productListingLoading = ref(false)
const productListingSubmitting = ref(false)
const categorySubmitting = ref(false)
const comboSubmitting = ref(false)
const extraSubmitting = ref(false)
const inventorySubmitting = ref(false)
const memberSubmitting = ref(false)
const adminSubmitting = ref(false)
const roleSubmitting = ref(false)
const systemParamsSubmitting = ref(false)
const paymentConfigSubmitting = ref(false)
const couponSubmitting = ref(false)
const bannerSubmitting = ref(false)
const rechargeSubmitting = ref(false)
const cardSubmitting = ref(false)
const groupBuySubmitting = ref(false)
const bargainSubmitting = ref(false)
const inviteSubmitting = ref(false)
const flashSubmitting = ref(false)
const droneSubmitting = ref(false)
const deletingStoreId = ref('')
const productListingVisible = ref(false)
const productListing = ref<ProductListing>()
const selectedListingProduct = ref<AdminProduct>()
const productListingStoreName = computed(() =>
  storeOptions.value.find((store) => store.id === productListing.value?.storeId)?.name ?? productListing.value?.storeId ?? '',
)

const orderFilters = reactive({ orderNo: '', phone: '', storeId: '', status: '' as OrderStatus | '', deliveryType: '' as DeliveryType | '', page: 1, pageSize: 10 })
const refundFilters = reactive({ page: 1, pageSize: 10 })
const adminFilters = reactive({ page: 1, pageSize: 10 })
const operationLogFilters = reactive({ page: 1, pageSize: 10 })
const storeFilters = reactive({ keywords: [] as string[], status: '' })
const productFilters = reactive<ProductListParams>({ keyword: '', categoryId: '', status: '', page: 1, pageSize: 10 })
const inventoryFilters = reactive<InventoryListParams>({ page: 1, pageSize: 20 })
const couponFilters = reactive({ page: 1, pageSize: 10 })
const droneDeviceFilters = reactive<DroneListParams>({ page: 1, pageSize: 10, keyword: '', status: undefined, online: undefined })
const droneTaskFilters = reactive<DroneTaskListParams>({ page: 1, pageSize: 10, keyword: '', status: undefined })
const droneFlightFilters = reactive<DroneFlightListParams>({ page: 1, pageSize: 10, keyword: '', status: undefined, droneNo: '' })
const droneFaultFilters = reactive<DroneFaultListParams>({ page: 1, pageSize: 10, droneId: '', status: undefined, level: '' })
const droneMaintenanceFilters = reactive<DroneMaintenanceListParams>({ page: 1, pageSize: 10, droneId: '', status: undefined, type: undefined })

const statusForm = reactive({ status: '' as OrderStatus | '', remark: '' })
const refundForm = reactive({ result: 'APPROVED' as 'APPROVED' | 'REJECTED', reason: '' })
/** 取消配送费原因（选填，最长 100 字），随订单详情弹窗编辑。 */
const deliveryFeeReason = ref('')
const storeSettingsForm = reactive({
  openTime: '',
  closeTime: '',
  suspendOrdering: false,
  suspendReason: '',
  pickupPrepareMinutes: 15,
  deliveryPrepareMinutes: 20,
})
const storeForm = reactive<StoreSavePayload>({
  id: undefined,
  name: '',
  imageUrl: '',
  category: '',
  categories: [],
  code: '',
  address: '',
  phone: '',
  longitude: undefined,
  latitude: undefined,
  businessStatus: 'OPEN',
  supportsDelivery: true,
  supportsDrone: false,
  openTime: '08:00',
  closeTime: '22:00',
})
const productForm = reactive<ProductSavePayload>({
  id: undefined,
  storeId: '',
  name: '',
  categoryId: '',
  subtitle: '',
  description: '',
  mainImageUrl: '',
  originalPrice: undefined,
  status: 'ON',
  supportsDelivery: true,
  stock: undefined,
  specGroups: [],
})
/** 当前编辑商品的规格：多规格商品的库存是一个商品级数字，保存时按这些规格平摊。 */
const productSkus = ref<AdminSku[]>([])
/** 打开编辑弹窗时的库存值：用来判断运营是否真的改过库存，没改就不动各规格已有分布。 */
const productStockOnOpen = ref<number | undefined>(undefined)
const categoryForm = reactive<CategorySavePayload>({
  id: undefined,
  storeId: '',
  storeIds: [],
  name: '',
  tab: MenuTab.Classic,
  sort: 0,
  status: 'ENABLED',
  supportsDelivery: true,
})
const comboForm = reactive<ComboSavePayload>({
  id: undefined,
  storeId: '',
  name: '',
  originalPrice: 0,
  salePrice: 0,
  status: 'ENABLED',
  items: [],
})
const comboItemForm = reactive<AdminComboItem>({
  productId: '',
  productName: '',
  quantity: 1,
})
const extraForm = reactive<ExtraSavePayload>({
  id: undefined,
  name: '',
  price: 0,
  recommended: false,
  enabled: true,
  sort: 0,
})
const inventoryForm = reactive<InventoryUpdatePayload & { currentStock?: number }>({
  skuId: '',
  storeId: '',
  stock: undefined as unknown as number,
  currentStock: undefined,
})
const memberLevelForm = reactive<MemberLevelConfig>({
  id: undefined,
  code: '',
  name: '',
  growthThreshold: 0,
  discountRate: 1,
  pointsMultiplier: 1,
  badge: '',
  sort: 0,
})
function getGrowthValuePerYuan(param?: string): number {
  const match = param?.match(/得\s*(\d+(?:\.\d+)?)\s*成长值/)
  return match ? Number(match[1]) : 1
}

const growthRuleForm = reactive<GrowthRuleEditorForm>({
  id: undefined,
  ruleType: '消费',
  param: '',
  enabled: true,
  growthValuePerYuan: 1,
})
const pointsRuleForm = reactive<PointsRuleConfig>({
  id: undefined,
  ruleType: '消费',
  calcMethod: '每消费 1 元得 1 积分',
  value: 1,
  maxUsePerOrder: 0,
  enabled: true,
})
const memberBenefitForm = reactive<MemberBenefitConfig>({
  id: undefined,
  levelCode: '',
  benefitType: '',
  config: '',
  enabled: true,
  sort: 0,
})
const cardTemplateStoreIds = ref<string[]>([])
const couponStoreIds = ref<string[]>([])
const bannerStoreId = ref('')
const rechargePackageGiftCouponTemplateIds = ref<string[]>([])
const adminForm = reactive<AdminSavePayload>({
  id: undefined,
  account: '',
  password: '',
  nickname: '',
  roleIds: [],
  storeIds: [],
  status: 'ENABLED',
})
const roleForm = reactive<RoleSavePayload>({
  id: undefined,
  name: '',
  code: '',
  dataScope: 'ALL',
  permissions: [],
})
const adminRoleIdsTouched = ref(false)
const systemParamForm = reactive<SystemParamSavePayload>({
  id: undefined,
  key: '',
  value: '',
  groupName: 'GENERAL',
  remark: '',
})
const paymentConfigForm = reactive<PaymentConfigSavePayload>({
  id: undefined,
  channel: '',
  appId: '',
  mchId: '',
  apiKey: '',
  apiV3Key: '',
  notifyUrl: '',
  enabled: true,
})
const couponForm = reactive<CouponSavePayload>({
  id: undefined,
  name: '',
  type: 'FULL_REDUCTION',
  amount: 0,
  discountRate: undefined,
  threshold: 0,
  validType: 'DATE_RANGE',
  validFrom: '',
  validTo: '',
  validDays: undefined,
  totalCount: 0,
  perUserLimit: 1,
  storeIds: '',
  productScope: 'ALL',
  status: 'ENABLED',
})
const couponIssueForm = reactive<CouponIssuePayload>({
  couponId: '',
  targetType: 'ALL',
  targetIds: [],
  count: 1,
})
const bannerForm = reactive<BannerSavePayload>({
  id: undefined,
  title: '',
  imageUrl: '',
  linkType: '',
  linkValue: '',
  sort: 0,
  enabled: true,
  startAt: '',
  endAt: '',
})
const rechargePackageForm = reactive<RechargePackageSavePayload>({
  id: undefined,
  name: '',
  amount: 0,
  giftBalance: 0,
  giftCouponTemplateIds: '',
  sort: 0,
  enabled: true,
})
const cardTemplateForm = reactive<CardTemplateSavePayload>({
  id: undefined,
  name: '',
  category: 'COUNT_CARD',
  salePrice: 0,
  faceValue: 0,
  validDays: 30,
  countValue: 0,
  benefit: '',
  totalCount: 0,
  perUserLimit: 1,
  storeIds: '',
  status: 'ENABLED',
})
const groupBuyForm = reactive<GroupBuySavePayload>({
  id: undefined,
  name: '',
  productId: '',
  groupPrice: 0,
  teamSize: 2,
  durationHours: 24,
  storeIds: '',
  status: 'DRAFT',
  startAt: '',
  endAt: '',
})
const bargainForm = reactive<BargainSavePayload>({
  id: undefined,
  name: '',
  productId: '',
  floorPrice: 0,
  initPrice: 0,
  helpTimes: 3,
  storeId: '',
  status: 'DRAFT',
})
const inviteForm = reactive<InviteConfigSavePayload>({
  id: undefined,
  scene: 'REGISTER',
  inviterCouponTemplateId: '',
  inviteeCouponTemplateId: '',
  maxTimes: 1,
  enabled: true,
})
const flashSaleForm = reactive<FlashSaleSavePayload>({
  id: undefined,
  name: '',
  storeId: '',
  startAt: '',
  endAt: '',
  status: 'DRAFT',
  items: [],
})
const flashSaleItemForm = reactive<FlashSaleItem>({
  productId: '',
  productName: '',
  skuId: '',
  salePrice: 0,
  limitQuantity: 1,
  stock: 0,
  sold: 0,
})
const couponIssueTargetUserIds = ref<string[]>([])
const droneStoreIds = ref<string[]>([])
const comboStoreIds = ref<string[]>([])
const categoryStoreIds = ref<string[]>([])
const inventoryStoreIds = ref<string[]>([])
const groupBuyStoreIds = ref<string[]>([])
const bargainStoreIds = ref<string[]>([])
const flashSaleStoreIds = ref<string[]>([])
const droneForm = reactive<DroneSavePayload>({
  droneNo: '',
  name: '',
  model: '',
  deviceType: '',
  warehouseCode: '',
  storeId: '',
  battery: 100,
  status: 'IDLE',
  remark: '',
})

const { config, refreshButtonText, isCreateAction } = useOperationPageConfig(designKey, pageTitle)
const {
  loadingUsers,
  loadingUserWalletLogs,
  loadingUserPointsLogs,
  loadingMemberProfile,
  userPage,
  userWalletLogPage,
  userPointsLogPage,
  selectedMemberProfile,
  selectedConsumerUser,
  userManagerVisible,
  userBalanceEditorVisible,
  userPointsEditorVisible,
  memberEditorVisible,
  userBalanceSubmitting,
  userPointsSubmitting,
  memberProfileSubmitting,
  userAdminPermissionSubmitting,
  userFilters,
  userBalanceForm,
  userPointsForm,
  memberForm,
  userRows,
  userTotalPages,
  userManagementStats,
  userBalanceStats,
  userPointsStats,
  userAssetsStats,
  loadUsers,
  loadUserAssets,
  searchUsers,
  resetUserFilters,
  changeUserPage,
  changeUserAssetPage,
  openUserManager,
  openMemberEditor,
  openUserBalanceEditor,
  openUserPointsEditor,
  hasUserAdminOrderPermission,
  toggleUserStatus,
  removeUser,
  submitUserBalance,
  submitUserPoints,
  submitMemberProfile,
  submitUserAdminPermission,
  removeMemberProfile,
} = useUserCenter({
  isUserManagementCenter,
  isUserBalanceCenter,
  isUserPointsCenter,
  isUserAssetsCenter,
  formatAmount,
  formatSignedAmount,
  formatSignedNumber,
})
const primaryHeaderAction = computed<HeaderAction | undefined>(() => {
  if (!canOperateCurrentModule.value) return undefined
  if (isStoreCenter.value && userStore.isPlatformScope) return { label: '新增门店', icon: 'plus', handler: openCreateStore }
  if (isProductList.value) return { label: '新增商品', icon: 'plus', handler: openCreateProduct }
  if (isCategoryCenter.value && userStore.isPlatformScope) return { label: '新增分类', icon: 'plus', handler: openCreateCategory }
  if (isComboCenter.value) return { label: '新增套餐', icon: 'plus', handler: () => openComboEditor() }
  if (isExtraCenter.value) return { label: '新增加料', icon: 'plus', handler: openCreateExtra }
  if (isInventoryCenter.value) return { label: '库存调整', icon: 'edit', handler: () => openInventoryEditor() }
  if (isDeliveryRulesCenter.value || isDeliveryMinAmountCenter.value) return { label: '新增规则', icon: 'plus', handler: () => openDeliveryRuleEditor() }
  if (isDeliveryAreasCenter.value) return { label: '新增范围', icon: 'plus', handler: () => openDeliveryAreaEditor() }
  if (isDeliveryPointsCenter.value) return { label: '新增配送点', icon: 'plus', handler: () => openDeliveryPointEditor() }
  if (isUserBalanceCenter.value) return { label: '余额调整', icon: 'edit', handler: () => openUserBalanceEditor() }
  if (isUserPointsCenter.value) return { label: '积分调整', icon: 'edit', handler: () => openUserPointsEditor() }
  if (isMemberLevelsCenter.value) return { label: '新增等级', icon: 'plus', handler: () => openMemberLevelEditor() }
  if (isGrowthRulesCenter.value) return { label: '新增规则', icon: 'plus', handler: () => openGrowthRuleEditor() }
  if (isPointsRulesCenter.value) return { label: '新增规则', icon: 'plus', handler: () => openPointsRuleEditor() }
  if (isMemberBenefitsCenter.value) return { label: '新增权益', icon: 'plus', handler: () => openMemberBenefitEditor() }
  if (isMemberRulesCenter.value) return {
    label: '新增规则',
    icon: 'plus',
    handler: () => openGrowthRuleEditor(),
    items: [
      { label: '新增成长规则', handler: () => openGrowthRuleEditor() },
      { label: '新增积分规则', handler: () => openPointsRuleEditor() }
    ]
  }
  if (isSystemAdminCenter.value) return { label: '新增管理员', icon: 'plus', handler: () => openAdminEditor() }
  if (isRoleCenter.value) return { label: '新增角色', icon: 'plus', handler: () => openRoleEditor() }
  if (isSystemParamsCenter.value) return { label: '新增参数', icon: 'plus', handler: () => openSystemParamEditor() }
  if (isPaymentConfigCenter.value) return { label: '新增配置', icon: 'plus', handler: () => openPaymentConfigEditor() }
  if (isCouponCenter.value) return { label: '新建优惠券', icon: 'plus', handler: () => openCouponEditor() }
  if (isPointsMallCenter.value) return { label: '新增兑换项', icon: 'plus', handler: () => pointsMallPanelRef.value?.openCreateItem() }
  if (isBannerCenter.value) return { label: '新增 Banner', icon: 'plus', handler: () => openBannerEditor() }
  if (isCardCenter.value) return { label: '新建卡券', icon: 'plus', handler: () => openCardTemplateEditor() }
  if (isRechargeCenter.value && userStore.isPlatformScope) return { label: '新增套餐', icon: 'plus', handler: () => openRechargePackageEditor() }
  if (isGroupBuyCenter.value) return { label: '创建拼团', icon: 'plus', handler: () => openGroupBuyEditor() }
  if (isBargainCenter.value) return { label: '创建砍价', icon: 'plus', handler: () => openBargainEditor() }
  if (isInviteCenter.value && userStore.isPlatformScope) return { label: '配置奖励', icon: 'plus', handler: () => openInviteEditor() }
  if (isFlashSaleCenter.value) return { label: '创建秒杀', icon: 'plus', handler: () => openFlashSaleEditor() }
  if (isVerificationCenter.value) return { label: '执行核销', icon: 'edit', handler: () => openVerificationDialog() }
  if (isPrinterCenter.value) return { label: '新增设备', icon: 'plus', handler: () => openPrinterEditor() }
  if (isMessageTemplatesCenter.value) return { label: '新增模板', icon: 'plus', handler: () => openMessageTemplateEditor() }
  if (isDroneDevicesCenter.value) return { label: '新增设备', icon: 'plus', handler: openCreateDrone }
  if (isProfitSharingRulesCenter.value) return { label: '新增规则', icon: 'plus', handler: () => profitSharingPanelRef.value?.openRuleEditor() }
  if (isProfitSharingReceiversCenter.value) return { label: '新增接收方', icon: 'plus', handler: () => profitSharingPanelRef.value?.openReceiverEditor() }
  return undefined
})

function handleFallbackHeaderAction(): void {
  if (fallbackPanelRef.value) {
    fallbackPanelRef.value.openCreateAction()
    return
  }
  ElMessage.info('当前模块暂未配置快捷操作')
}

const isEditingStore = computed(() => Boolean(storeForm.id))
const isEditingProduct = computed(() => Boolean(productForm.id))
const isEditingCategory = computed(() => Boolean(categoryForm.id))
const isEditingCombo = computed(() => Boolean(comboForm.id))
const isEditingExtra = computed(() => Boolean(extraForm.id))
const isEditingMemberLevel = computed(() => Boolean(memberLevelForm.id))
const isEditingGrowthRule = computed(() => Boolean(growthRuleForm.id))
const isEditingPointsRule = computed(() => Boolean(pointsRuleForm.id))
const isEditingMemberBenefit = computed(() => Boolean(memberBenefitForm.id))
const isEditingAdmin = computed(() => Boolean(adminForm.id))
const isEditingRole = computed(() => Boolean(roleForm.id))
const isEditingSystemParam = computed(() => Boolean(systemParamForm.id))
const isEditingPaymentConfig = computed(() => Boolean(paymentConfigForm.id))
const isEditingCoupon = computed(() => Boolean(couponForm.id))
const isEditingBanner = computed(() => Boolean(bannerForm.id))
const isEditingRechargePackage = computed(() => rechargePackageEditing.value)
const isEditingCardTemplate = computed(() => Boolean(cardTemplateForm.id))
const isEditingGroupBuy = computed(() => Boolean(groupBuyForm.id))
const isEditingBargain = computed(() => Boolean(bargainForm.id))
const isEditingInvite = computed(() => Boolean(inviteForm.id))
const isEditingFlashSale = computed(() => Boolean(flashSaleForm.id))

const toneClass = (tone?: string) => `is-${tone ?? 'muted'}`
const getBannerStatusText = (enabled?: boolean) => enabled === false ? '停用' : '启用'
const bannerStatusTone = (enabled?: boolean) => enabled === false ? 'muted' as const : 'success' as const
function getBannerLinkText(banner: AdminBanner): string {
  if (!banner.linkType && !banner.linkValue) return '不跳转'
  const typeText = banner.linkType === 'PRODUCT'
    ? '商品'
    : banner.linkType === 'ACTIVITY'
      ? '活动'
      : banner.linkType === 'PAGE'
        ? '页面'
        : banner.linkType || '跳转'
  return `${typeText}: ${banner.linkValue || '--'}`
}
const {
  loadingDataOverview,
  loadingDataOrders,
  loadingDataProducts,
  loadingStoreRank,
  dataOverviewData,
  dataOrderAnalysis,
  dataProductAnalysis,
  storeRankRows,
  dataOverviewStats,
  dataOrderStats,
  dataProductStats,
  storeRankStats,
  loadDataOverview,
  loadDataOrders,
  loadDataProducts,
  loadStoreRank,
} = useDataCenter({
  isDataOverviewCenter,
  isDataOrdersCenter,
  isDataProductsCenter,
  isStoreRankCenter,
  formatAmount,
})
const {
  loadingTransactions,
  loadingReconciliation,
  loadingRefundTransactions,
  transactionPage,
  reconciliationPage,
  refundTransactionPage,
  transactionTotalPages,
  reconciliationTotalPages,
  refundTransactionTotalPages,
  transactionStats,
  reconciliationStats,
  refundTransactionStats,
  loadTransactions,
  loadReconciliation,
  loadRefundTransactions,
  changeTransactionPage,
  changeReconciliationPage,
  changeRefundTransactionPage,
} = useFinanceCenter({
  isTransactionsCenter,
  isReconciliationCenter,
  isRefundTransactionsCenter,
  formatAmount,
})
const {
  loadingVerificationLogs,
  verificationVisible,
  verificationSubmitting,
  verificationLogPage,
  verificationFilters,
  verificationForm,
  verificationLogTotalPages,
  verificationStats,
  loadVerificationLogs,
  searchVerificationLogs,
  resetVerificationFilters,
  changeVerificationPage,
  openVerificationDialog,
  submitVerification,
} = useVerificationLogs(isVerificationCenter)
const {
  loadingPrinters,
  printerRows,
  printerEditorVisible,
  printerSubmitting,
  printerForm,
  printerStoreId,
  printerStats,
  loadPrinters,
  openPrinterEditor,
  submitPrinter,
  removePrinter,
} = usePrinterCenter({
  isPrinterCenter,
  ensureEditorOptions,
})
const {
  loadingMessageLogs,
  loadingMessageTemplates,
  loadingMessageConfig,
  messageTemplateEditorVisible,
  messageTemplateSubmitting,
  messageConfigSubmitting,
  messageLogPage,
  messageTemplates,
  messageConfigRows,
  messageTemplateForm,
  messageLogTotalPages,
  messageLogStats,
  messageTemplateStats,
  messageConfigStats,
  loadMessageLogs,
  loadMessageTemplates,
  loadMessageConfig,
  changeMessageLogPage,
  openMessageTemplateEditor,
  submitMessageTemplate,
  toggleMessageConfig,
} = useMessageCenter({
  isMessageLogsCenter,
  isMessageTemplatesCenter,
  isMessageConfigCenter,
})
const {
  loadingDeliveryRules,
  loadingDeliveryAreas,
  loadingDeliveryPoints,
  deliveryRuleEditorVisible,
  deliveryAreaEditorVisible,
  deliveryPointEditorVisible,
  deliveryPointLocationVisible,
  deliveryRuleSubmitting,
  deliveryAreaSubmitting,
  deliveryPointSubmitting,
  deliveryRules,
  deliveryAreas,
  deliveryPoints,
  selectedDeliveryPoint,
  deliveryRuleForm,
  deliveryAreaForm,
  deliveryPointForm,
  deliveryRuleStoreIds,
  deliveryAreaStoreIds,
  deliveryRuleStats,
  deliveryMinAmountStats,
  deliveryAreaStats,
  deliveryPointStats,
  loadDeliveryRules,
  loadDeliveryAreas,
  loadDeliveryPoints,
  openDeliveryRuleEditor,
  openDeliveryAreaEditor,
  openDeliveryPointEditor,
  openDeliveryPointLocation,
  submitDeliveryRule,
  submitDeliveryArea,
  submitDeliveryPoint,
  removeDeliveryArea,
  removeDeliveryRule,
  removeDeliveryPoint,
} = useDeliveryCenter({
  isDeliveryRulesCenter,
  isDeliveryMinAmountCenter,
  isDeliveryAreasCenter,
  isDeliveryPointsCenter,
  formatAmount,
  ensureEditorOptions: ensureStoreOptions,
})

const orderFilterStatusOptions = Object.values(OrderStatus).map((value) => ({ value, label: ORDER_STATUS_LABELS[value] }))
const orderStatusOptions = computed(() => {
  const currentStatus = selectedOrder.value?.status
  if (!currentStatus) return []
  return getAllowedOrderStatusTransitions(currentStatus).map((value) => ({ value, label: ORDER_STATUS_LABELS[value] }))
})
// 业务只提供门店自取与无人机配送，不展示骑手配送
const deliveryTypeOptions = [
  { value: DeliveryType.Pickup, label: '门店自取' },
  { value: DeliveryType.Drone, label: '无人机配送' },
]
const storeStatusOptions = [
  { value: 'OPEN', label: '营业中' },
  { value: 'PAUSED', label: '暂停营业' },
  { value: 'CLOSED', label: '已闭店' },
]

const orderRows = computed(() => orderPage.value?.list ?? [])
const refundRows = computed(() => refundPage.value?.list ?? [])
const productRows = computed(() => productPage.value?.list ?? [])
const couponRows = computed(() => couponPage.value?.list ?? [])
const couponSelectOptions = computed(() => couponOptions.value.length ? couponOptions.value : couponRows.value)
const inventoryTotalPages = computed(() => !inventoryPage.value ? 1 : Math.max(1, Math.ceil(inventoryPage.value.total / inventoryPage.value.pageSize)))
const orderTotalPages = computed(() => !orderPage.value ? 1 : Math.max(1, Math.ceil(orderPage.value.total / orderPage.value.pageSize)))
const refundTotalPages = computed(() => !refundPage.value ? 1 : Math.max(1, Math.ceil(refundPage.value.total / refundPage.value.pageSize)))
const complaintTotalPages = computed(() => !complaintPage.value ? 1 : Math.max(1, Math.ceil(complaintPage.value.total / complaintPage.value.pageSize)))
const productTotalPages = computed(() => !productPage.value ? 1 : Math.max(1, Math.ceil(productPage.value.total / productPage.value.pageSize)))
const couponTotalPages = computed(() => !couponPage.value ? 1 : Math.max(1, Math.ceil(couponPage.value.total / couponPage.value.pageSize)))
const adminTotalPages = computed(() => !adminPage.value ? 1 : Math.max(1, Math.ceil(adminPage.value.total / adminPage.value.pageSize)))
const operationLogTotalPages = computed(() => !operationLogPage.value ? 1 : Math.max(1, Math.ceil(operationLogPage.value.total / operationLogPage.value.pageSize)))

const droneStatusTextMap: Record<Drone['status'], string> = {
  IDLE: '空闲',
  TASK: '任务中',
  CHARGING: '充电中',
  MAINTENANCE: '维护中',
  FAULT: '故障',
  OFFLINE: '离线',
}
const droneTaskStatusTextMap: Record<DroneTask['status'], string> = {
  WAITING: '待调度',
  DRONE_LOADING: '装载中',
  DRONE_TAKEOFF: '已起飞',
  DRONE_FLYING: '配送中',
  ARRIVED: '已到达',
  COMPLETED: '已完成',
  EXCEPTION: '异常',
}
const droneFaultStatusTextMap: Record<DroneFault['status'], string> = {
  OPEN: '待处理',
  PROCESSING: '处理中',
  RESOLVED: '已解决',
}
const droneMaintenanceStatusTextMap: Record<DroneMaintenance['status'], string> = {
  SCHEDULED: '计划中',
  DOING: '执行中',
  DONE: '已完成',
}
const droneMaintenanceTypeTextMap: Record<DroneMaintenance['type'], string> = {
  REPAIR: '维修',
  MAINTAIN: '保养',
  INSPECT: '巡检',
}
const getDroneStatusText = (status: string): string => droneStatusTextMap[status as Drone['status']] ?? status
const getDroneTaskStatusText = (status: string): string => droneTaskStatusTextMap[status as DroneTask['status']] ?? status
const getDroneFaultStatusText = (status: string): string => droneFaultStatusTextMap[status as DroneFault['status']] ?? status
const getDroneMaintenanceStatusText = (status: string): string => droneMaintenanceStatusTextMap[status as DroneMaintenance['status']] ?? status
const getDroneMaintenanceTypeText = (type: string): string => droneMaintenanceTypeTextMap[type as DroneMaintenance['type']] ?? type
const droneStatusTone = (status: string) => status === 'IDLE' ? 'success' as const : status === 'TASK' ? 'brand' as const : status === 'FAULT' ? 'error' as const : status === 'OFFLINE' ? 'muted' as const : 'warning' as const
const droneTaskStatusTone = (status: string) => status === 'COMPLETED' || status === 'ARRIVED' ? 'success' as const : status === 'EXCEPTION' ? 'error' as const : status === 'WAITING' ? 'warning' as const : 'brand' as const
const droneFaultStatusTone = (status: string) => status === 'RESOLVED' ? 'success' as const : status === 'PROCESSING' ? 'brand' as const : 'warning' as const
const droneMaintenanceStatusTone = (status: string) => status === 'DONE' ? 'success' as const : status === 'DOING' ? 'brand' as const : 'warning' as const
const droneTotalPages = computed(() => {
  const page = isDroneDevicesCenter.value
    ? dronePage.value
    : isDroneTasksCenter.value
      ? droneTaskPage.value
      : isDroneFlightsCenter.value
        ? droneFlightPage.value
        : isDroneFaultsCenter.value
          ? droneFaultPage.value
          : droneMaintenancePage.value
  return !page ? 1 : Math.max(1, Math.ceil(page.total / page.pageSize))
})
const droneStats = computed<OperationStat[]>(() => {
  if (!isDroneDevicesCenter.value) {
    const page = isDroneTasksCenter.value
      ? droneTaskPage.value
      : isDroneFlightsCenter.value
        ? droneFlightPage.value
        : isDroneFaultsCenter.value
          ? droneFaultPage.value
          : droneMaintenancePage.value
    return [
      { label: '当前页记录', value: String(page?.list.length ?? 0), sub: '分页结果', tone: 'brand' },
      { label: '记录总数', value: String(page?.total ?? 0), sub: '实时查询', tone: 'success' },
      { label: '页码', value: String(page?.page ?? 1), sub: `每页 ${page?.pageSize ?? 10} 条`, tone: 'warning' },
    ]
  }
  const overview = droneOverview.value
  return [
    { label: '设备总数', value: String(overview?.total ?? dronePage.value?.total ?? 0), sub: '台账设备', tone: 'brand' },
    { label: '在线设备', value: String(overview?.onlineCount ?? 0), sub: 'IDLE/TASK 视为在线', tone: 'success' },
    { label: '任务中', value: String(overview?.taskCount ?? 0), sub: '当前执行配送', tone: 'warning' },
    { label: '故障设备', value: String(overview?.faultCount ?? 0), sub: '需运维处理', tone: 'error' },
  ]
})

const orderStats = computed(() => {
  const list = orderRows.value
  const pendingCount = list.filter((item) => [OrderStatus.PendingAccept, OrderStatus.Producing, OrderStatus.PendingPickup, OrderStatus.Delivering].includes(item.status)).length
  const refundingCount = list.filter((item) => [OrderStatus.Refunding, OrderStatus.Refunded].includes(item.status)).length
  return [
    { label: '当前页订单', value: String(list.length), sub: '分页结果', tone: 'brand' as const },
    { label: '订单总数', value: String(orderPage.value?.total ?? 0), sub: '实时查询', tone: 'success' as const },
    { label: '待处理订单', value: String(pendingCount), sub: '当前页统计', tone: 'warning' as const },
    { label: '退款相关订单', value: String(refundingCount), sub: '当前页统计', tone: 'error' as const },
  ]
})

const refundStats = computed(() => {
  const list = refundRows.value
  const pendingCount = list.filter((item) => canAuditRefund(item.status)).length
  const rejectedCount = list.filter((item) => item.status === RefundStatus.Rejected).length
  const refundAmount = list.reduce((total, item) => total + item.amount, 0)
  return [
    { label: '当前页退款单', value: String(list.length), sub: '分页结果', tone: 'brand' as const },
    { label: '待审核退款', value: String(pendingCount), sub: '优先处理', tone: 'warning' as const },
    { label: '当前页退款金额', value: formatAmount(refundAmount), sub: '金额已换算为元', tone: 'error' as const },
    { label: '已驳回退款', value: String(rejectedCount), sub: '当前页统计', tone: 'success' as const },
  ]
})

const complaintStats = computed(() => {
  const list = complaintRows.value
  return [
    { label: '投诉建议总数', value: String(complaintPage.value?.total ?? 0), sub: '后端分页总量', tone: 'brand' as const },
    { label: '当前页待处理', value: String(list.filter((item) => item.status === 'PENDING').length), sub: '需要跟进', tone: 'warning' as const },
    { label: '当前页投诉', value: String(list.filter((item) => item.type === 'COMPLAINT').length), sub: '用户投诉', tone: 'error' as const },
    { label: '当前页建议', value: String(list.filter((item) => item.type === 'SUGGESTION').length), sub: '体验建议', tone: 'success' as const },
  ]
})

const storeStatsSummary = computed(() => {
  const openCount = storeRows.value.filter((item) => item.businessStatus === 'OPEN').length
  const deliveryCount = storeRows.value.filter((item) => item.supportsDelivery).length
  const droneCount = storeRows.value.filter((item) => item.supportsDrone).length
  return [
    { label: '门店总数', value: String(storeRows.value.length), sub: '实时查询', tone: 'brand' as const },
    { label: '营业中门店', value: String(openCount), sub: '当前可接单', tone: 'success' as const },
    { label: '支持配送', value: String(deliveryCount), sub: '门店配送能力', tone: 'warning' as const },
    { label: '支持无人机', value: String(droneCount), sub: '空中配送能力', tone: 'brand' as const },
  ]
})

const productStats = computed(() => {
  const list = productRows.value
  const onSaleCount = list.filter((item) => item.status === 'ON').length
  const soldOutCount = list.filter((item) => (item.stock ?? 0) <= 0).length
  const totalSales = list.reduce((total, item) => total + (item.sales ?? 0), 0)
  return [
    { label: '商品总数', value: String(productPage.value?.total ?? 0), sub: '实时查询', tone: 'brand' as const },
    { label: '当前页在售', value: String(onSaleCount), sub: '分页结果', tone: 'success' as const },
    { label: '当前页售罄', value: String(soldOutCount), sub: '库存为 0', tone: 'warning' as const },
    { label: '当前页销量', value: String(totalSales), sub: '累计销量', tone: 'brand' as const },
  ]
})

const comboStats = computed(() => {
  const enabledCount = combos.value.filter((item) => item.status === 'ENABLED').length
  const itemCount = combos.value.reduce((total, item) => total + (item.items?.length ?? 0), 0)
  return [
    { label: '套餐数量', value: String(combos.value.length), sub: '实时查询', tone: 'brand' as const },
    { label: '启用套餐', value: String(enabledCount), sub: '当前可售', tone: 'success' as const },
    { label: '套餐商品', value: String(itemCount), sub: '明细项数量', tone: 'warning' as const },
    { label: '平均商品数', value: String(combos.value.length ? Math.round(itemCount / combos.value.length) : 0), sub: '每个套餐', tone: 'brand' as const },
  ]
})

const inventoryStats = computed(() => {
  const list = inventoryRows.value
  const warningCount = list.filter((item) => item.safetyStock !== undefined && item.stock <= item.safetyStock).length
  const soldOutCount = list.filter((item) => item.stock <= 0).length
  return [
    { label: '库存 SKU', value: String(inventoryPage.value?.total ?? 0), sub: '实时查询', tone: 'brand' as const },
    { label: '当前页预警', value: String(warningCount), sub: '低于安全库存', tone: 'warning' as const },
    { label: '当前页售罄', value: String(soldOutCount), sub: '库存为 0', tone: 'error' as const },
    { label: '当前页正常', value: String(list.length - warningCount - soldOutCount), sub: '可继续售卖', tone: 'success' as const },
  ]
})

const memberStats = computed(() => {
  if (isMemberLevelsCenter.value) {
    return [
      { label: '等级数量', value: String(memberLevels.value.length), sub: '会员分层配置', tone: 'brand' as const },
      { label: '最高门槛', value: String(Math.max(0, ...memberLevels.value.map((item) => item.growthThreshold ?? 0))), sub: '成长值要求', tone: 'success' as const },
      { label: '最高积分倍率', value: String(Math.max(0, ...memberLevels.value.map((item) => Number(item.pointsMultiplier ?? 0)))), sub: '积分返还', tone: 'warning' as const },
      { label: '默认折扣', value: String(Math.min(...(memberLevels.value.length ? memberLevels.value.map((item) => Number(item.discountRate ?? 1)) : [1]))), sub: '折扣系数', tone: 'brand' as const },
    ]
  }
  if (isGrowthRulesCenter.value) {
    return [
      { label: '规则数量', value: String(growthRules.value.length), sub: '成长值场景', tone: 'brand' as const },
      { label: '启用规则', value: String(growthRules.value.filter((item) => item.enabled).length), sub: '当前生效', tone: 'success' as const },
      { label: '停用规则', value: String(growthRules.value.filter((item) => item.enabled === false).length), sub: '待调整', tone: 'warning' as const },
      { label: '配置状态', value: '实时', sub: '来自后端', tone: 'brand' as const },
    ]
  }
  if (isPointsRulesCenter.value) {
    return [
      { label: '规则数量', value: String(pointsRules.value.length), sub: '积分场景', tone: 'brand' as const },
      { label: '启用规则', value: String(pointsRules.value.filter((item) => item.enabled).length), sub: '当前生效', tone: 'success' as const },
      { label: '最大单单抵扣', value: String(Math.max(0, ...pointsRules.value.map((item) => item.maxUsePerOrder ?? 0))), sub: '上限配置', tone: 'warning' as const },
      { label: '最高积分值', value: String(Math.max(0, ...pointsRules.value.map((item) => item.value ?? 0))), sub: '单规则值', tone: 'brand' as const },
    ]
  }
  return [
    { label: '权益数量', value: String(memberBenefits.value.length), sub: '等级权益配置', tone: 'brand' as const },
    { label: '启用权益', value: String(memberBenefits.value.filter((item) => item.enabled).length), sub: '当前生效', tone: 'success' as const },
    { label: '停用权益', value: String(memberBenefits.value.filter((item) => item.enabled === false).length), sub: '待上线', tone: 'warning' as const },
    { label: '覆盖等级', value: String(new Set(memberBenefits.value.map((item) => item.levelCode).filter(Boolean)).size), sub: '权益等级数', tone: 'brand' as const },
  ]
})

const adminStats = computed(() => {
  const list = adminRows.value
  return [
    { label: '管理员总数', value: String(adminPage.value?.total ?? 0), sub: '实时查询', tone: 'brand' as const },
    { label: '当前页启用', value: String(list.filter((item) => item.status === 'ENABLED').length), sub: '可登录账号', tone: 'success' as const },
    { label: '当前页停用', value: String(list.filter((item) => item.status === 'DISABLED').length), sub: '已停用账号', tone: 'warning' as const },
    { label: '已分配角色', value: String(list.filter((item) => item.roles?.length).length), sub: '当前页统计', tone: 'brand' as const },
  ]
})

const roleStats = computed(() => {
  const list = systemRoles.value
  const permissionCount = list.reduce((total, item) => total + (item.permissions?.length ?? 0), 0)
  return [
    { label: '角色数量', value: String(list.length), sub: '实时查询', tone: 'brand' as const },
    { label: '启用角色', value: String(list.filter((item) => item.status === 'ENABLED').length), sub: '当前生效', tone: 'success' as const },
    { label: '权限数量', value: String(permissionCount), sub: '角色权限总项', tone: 'warning' as const },
    { label: '全部数据角色', value: String(list.filter((item) => item.dataScope === 'ALL').length), sub: '数据范围', tone: 'brand' as const },
  ]
})

const operationLogStats = computed(() => {
  const list = operationLogPage.value?.list ?? []
  return [
    { label: '日志总数', value: String(operationLogPage.value?.total ?? 0), sub: '实时查询', tone: 'brand' as const },
    { label: '当前页日志', value: String(list.length), sub: '分页结果', tone: 'success' as const },
    { label: '操作人数量', value: String(new Set(list.map((item) => item.operatorId || item.operatorName).filter(Boolean)).size), sub: '当前页统计', tone: 'warning' as const },
    { label: '涉及模块', value: String(new Set(list.map((item) => item.module).filter(Boolean)).size), sub: '当前页统计', tone: 'brand' as const },
  ]
})

const systemParamStats = computed(() => {
  const list = systemParams.value
  return [
    { label: '参数数量', value: String(list.length), sub: '实时查询', tone: 'brand' as const },
    { label: '参数分组', value: String(new Set(list.map((item) => item.groupName).filter(Boolean)).size), sub: '配置分类', tone: 'success' as const },
    { label: '已填写备注', value: String(list.filter((item) => item.remark).length), sub: '可维护参数', tone: 'warning' as const },
    { label: '可编辑', value: String(list.length), sub: '支持新增/修改', tone: 'brand' as const },
  ]
})

const paymentConfigStats = computed(() => {
  const list = paymentConfigs.value
  return [
    { label: '支付配置', value: String(list.length), sub: '实时查询', tone: 'brand' as const },
    { label: '已启用', value: String(list.filter((item) => item.enabled).length), sub: '当前生效', tone: 'success' as const },
    { label: '密钥已配置', value: String(list.filter((item) => item.configStatus === 'SET').length), sub: '脱敏状态', tone: 'warning' as const },
    { label: '支付渠道', value: String(new Set(list.map((item) => item.channel).filter(Boolean)).size), sub: '渠道数量', tone: 'brand' as const },
  ]
})

const couponStats = computed(() => {
  const list = couponRows.value
  const enabledCount = list.filter((item) => item.status === 'ENABLED').length
  const issuedCount = list.reduce((total, item) => total + (item.issuedCount ?? 0), 0)
  const stockCount = list.reduce((total, item) => total + (item.totalCount ?? 0), 0)
  return [
    { label: '当前页优惠券', value: String(list.length), sub: '分页结果', tone: 'brand' as const },
    { label: '启用优惠券', value: String(enabledCount), sub: '可发放', tone: 'success' as const },
    { label: '总库存', value: String(stockCount), sub: '当前页统计', tone: 'warning' as const },
    { label: '已发放', value: String(issuedCount), sub: '当前页统计', tone: 'brand' as const },
  ]
})

const bannerStats = computed(() => {
  const enabledCount = banners.value.filter((item) => item.enabled).length
  const linkedCount = banners.value.filter((item) => item.linkType && item.linkValue).length
  return [
    { label: 'Banner 数量', value: String(banners.value.length), sub: '实时查询', tone: 'brand' as const },
    { label: '启用 Banner', value: String(enabledCount), sub: '小程序可见', tone: 'success' as const },
    { label: '配置跳转', value: String(linkedCount), sub: '已配置链接', tone: 'warning' as const },
  ]
})

const rechargeStats = computed(() => {
  const enabledCount = rechargePackages.value.filter((item) => item.status === 'ENABLED').length
  const totalAmount = rechargePackages.value.reduce((total, item) => total + (item.amount ?? 0), 0)
  const totalGift = rechargePackages.value.reduce((total, item) => total + (item.giftBalance ?? 0), 0)
  return [
    { label: '套餐数量', value: String(rechargePackages.value.length), sub: '实时查询', tone: 'brand' as const },
    { label: '上架套餐', value: String(enabledCount), sub: '当前可售', tone: 'success' as const },
    { label: '充值总额', value: formatAmount(totalAmount), sub: '当前页统计', tone: 'warning' as const },
    { label: '赠送总额', value: formatAmount(totalGift), sub: '当前页统计', tone: 'brand' as const },
  ]
})

const cardStats = computed(() => {
  const enabledCount = cardTemplates.value.filter((item) => item.status === 'ENABLED').length
  const issuedCount = cardTemplates.value.reduce((total, item) => total + (item.issuedCount ?? 0), 0)
  return [
    { label: '模板数量', value: String(cardTemplates.value.length), sub: '实时查询', tone: 'brand' as const },
    { label: '启用模板', value: String(enabledCount), sub: '当前可售', tone: 'success' as const },
    { label: '已发放', value: String(issuedCount), sub: '累计发放', tone: 'warning' as const },
    { label: '平均限领', value: String(cardTemplates.value.length ? Math.round(cardTemplates.value.reduce((total, item) => total + (item.perUserLimit ?? 0), 0) / cardTemplates.value.length) : 0), sub: '每人限制', tone: 'brand' as const },
  ]
})

const groupBuyStats = computed(() => {
  const enabledCount = groupBuys.value.filter((item) => item.status === 'ENABLED').length
  const totalTeamSize = groupBuys.value.reduce((total, item) => total + (item.teamSize ?? 0), 0)
  return [
    { label: '活动数量', value: String(groupBuys.value.length), sub: '实时查询', tone: 'brand' as const },
    { label: '启用活动', value: String(enabledCount), sub: '当前生效', tone: 'success' as const },
    { label: '成团人数', value: String(totalTeamSize), sub: '配置总人数', tone: 'warning' as const },
    { label: '草稿活动', value: String(groupBuys.value.filter((item) => item.status === 'DRAFT').length), sub: '待发布', tone: 'brand' as const },
  ]
})

const bargainStats = computed(() => {
  const enabledCount = bargains.value.filter((item) => item.status === 'ENABLED').length
  const totalHelpTimes = bargains.value.reduce((total, item) => total + (item.helpTimes ?? 0), 0)
  return [
    { label: '活动数量', value: String(bargains.value.length), sub: '实时查询', tone: 'brand' as const },
    { label: '启用活动', value: String(enabledCount), sub: '当前生效', tone: 'success' as const },
    { label: '助力次数', value: String(totalHelpTimes), sub: '配置总次数', tone: 'warning' as const },
    { label: '草稿活动', value: String(bargains.value.filter((item) => item.status === 'DRAFT').length), sub: '待发布', tone: 'brand' as const },
  ]
})

const inviteStats = computed(() => {
  const enabledCount = inviteConfigs.value.filter((item) => item.enabled).length
  const totalLimit = inviteConfigs.value.reduce((total, item) => total + (item.maxTimes ?? 0), 0)
  return [
    { label: '配置数量', value: String(inviteConfigs.value.length), sub: '实时查询', tone: 'brand' as const },
    { label: '启用配置', value: String(enabledCount), sub: '当前生效', tone: 'success' as const },
    { label: '奖励上限', value: String(totalLimit), sub: '累计次数', tone: 'warning' as const },
    { label: '平均上限', value: String(inviteConfigs.value.length ? Math.round(totalLimit / inviteConfigs.value.length) : 0), sub: '单配置均值', tone: 'brand' as const },
  ]
})

const flashSaleStats = computed(() => {
  const enabledCount = flashSales.value.filter((item) => item.status === 'ENABLED').length
  const goodsCount = flashSales.value.reduce((total, item) => total + (item.items?.length ?? 0), 0)
  return [
    { label: '活动数量', value: String(flashSales.value.length), sub: '实时查询', tone: 'brand' as const },
    { label: '启用活动', value: String(enabledCount), sub: '当前生效', tone: 'success' as const },
    { label: '活动商品', value: String(goodsCount), sub: '全部秒杀商品', tone: 'warning' as const },
    { label: '草稿活动', value: String(flashSales.value.filter((item) => item.status === 'DRAFT').length), sub: '待发布', tone: 'brand' as const },
  ]
})

watch(designKey, async () => {
  await refreshCurrentPage()
}, { immediate: true })

async function refreshCurrentPage(): Promise<void> {
  if (isOrderCenter.value) {
    await Promise.all([loadOrders(), ensureStoreOptions()])
    return
  }
  if (isRefundCenter.value) return loadRefunds()
  if (isComplaintCenter.value) return loadComplaints()
  if (isDeliveryRulesCenter.value || isDeliveryMinAmountCenter.value) return loadDeliveryRules()
  if (isDeliveryAreasCenter.value) return loadDeliveryAreas()
  if (isDeliveryPointsCenter.value) return loadDeliveryPoints()
  if (isDroneDevicesCenter.value) return loadDroneDevices()
  if (isDroneTasksCenter.value) return loadDroneTasks()
  if (isDroneFlightsCenter.value) return loadDroneFlights()
  if (isDroneFaultsCenter.value) return loadDroneFaults()
  if (isDroneMaintenancesCenter.value) return loadDroneMaintenances()
  if (isUserManagementCenter.value || isUserBalanceCenter.value || isUserPointsCenter.value) {
    await Promise.all([
      loadUsers(),
      fetchMemberLevels().then((levels) => { memberLevels.value = levels }),
    ])
    return
  }
  if (isUserAssetsCenter.value) return loadUserAssets()
  if (isVerificationCenter.value) return loadVerificationLogs()
  if (isPrinterCenter.value) return loadPrinters()
  if (isMessageLogsCenter.value) return loadMessageLogs()
  if (isMessageTemplatesCenter.value) return loadMessageTemplates()
  if (isMessageConfigCenter.value) return loadMessageConfig()
  if (isDataOverviewCenter.value) return loadDataOverview()
  if (isDataOrdersCenter.value) return loadDataOrders()
  if (isDataProductsCenter.value) return loadDataProducts()
  if (isStoreRankCenter.value) return loadStoreRank()
  if (isTransactionsCenter.value) return loadTransactions()
  if (isReconciliationCenter.value) return loadReconciliation()
  if (isRefundTransactionsCenter.value) return loadRefundTransactions()
  if (isProfitSharingModule.value) return profitSharingPanelRef.value?.refresh()
  if (isStoreCenter.value) return loadStores()
  if (isProductList.value) {
    await ensureStoreOptions()
    applyDefaultProductStore()
    await Promise.all([loadProducts(), loadCategories()])
    return
  }
  if (isCategoryCenter.value) {
    await Promise.all([loadCategories(), ensureStoreOptions()])
    return
  }
  if (isComboCenter.value) {
    await Promise.all([loadCombos(), loadProductOptions()])
    return
  }
  if (isExtraCenter.value) return loadExtras()
  if (isInventoryCenter.value) return loadInventory()
  if (isMemberCenter.value) return loadMemberConfigs()
  if (isSystemCenter.value) {
    if (isSystemAdminCenter.value) {
      await Promise.all([loadAdmins(), loadRoles(), loadStores()])
      return
    }
    if (isRoleCenter.value) return loadRoles()
    if (isOperationLogsCenter.value) return loadOperationLogs()
    if (isSystemParamsCenter.value) return loadSystemParams()
    return loadPaymentConfigs()
  }
  if (isCouponCenter.value) return loadCoupons()
  if (isPointsMallCenter.value) {
    await Promise.all([
      pointsMallPanelRef.value?.loadItems(),
      pointsMallPanelRef.value?.loadExchanges(),
    ])
    return
  }
  if (isBannerCenter.value) return loadBanners()
  if (isCardCenter.value) return loadCardTemplates()
  if (isRechargeCenter.value) return loadRechargePackages()
  if (isGroupBuyCenter.value) return loadGroupBuys()
  if (isBargainCenter.value) return loadBargains()
  if (isInviteCenter.value) {
    await Promise.all([loadInviteConfigs(), loadCouponOptions()])
    return
  }
  if (isFlashSaleCenter.value) return loadFlashSales()
}

async function loadOrders(): Promise<void> {
  if (!isOrderCenter.value) return
  loadingOrders.value = true
  try {
    orderPage.value = await fetchOrders({
      page: orderFilters.page,
      pageSize: orderFilters.pageSize,
      orderNo: orderFilters.orderNo || undefined,
      phone: orderFilters.phone || undefined,
      storeId: orderFilters.storeId || undefined,
      status: orderFilters.status,
      deliveryType: orderFilters.deliveryType,
    })
  } catch (error) {
    ElMessage.error(error instanceof Error ? error.message : '订单列表加载失败')
  } finally {
    loadingOrders.value = false
  }
}

async function loadRefunds(): Promise<void> {
  if (!isRefundCenter.value) return
  loadingRefunds.value = true
  try {
    refundPage.value = await fetchRefunds({ page: refundFilters.page, pageSize: refundFilters.pageSize })
  } catch (error) {
    ElMessage.error(error instanceof Error ? error.message : '退款列表加载失败')
  } finally {
    loadingRefunds.value = false
  }
}

async function loadComplaints(): Promise<void> {
  if (!isComplaintCenter.value) return
  loadingComplaints.value = true
  try {
    complaintPage.value = await fetchComplaints({ page: complaintPage.value?.page ?? 1, pageSize: complaintPage.value?.pageSize ?? 10 })
  } catch (error) {
    ElMessage.error(error instanceof Error ? error.message : '投诉建议加载失败')
  } finally {
    loadingComplaints.value = false
  }
}

async function loadDroneDevices(): Promise<void> {
  loadingDrones.value = true
  try {
    const [overview, page] = await Promise.all([
      fetchDroneOverview(),
      fetchDrones({
        page: droneDeviceFilters.page,
        pageSize: droneDeviceFilters.pageSize,
        keyword: droneDeviceFilters.keyword || undefined,
        status: droneDeviceFilters.status,
        online: droneDeviceFilters.online,
      }),
    ])
    droneOverview.value = overview
    dronePage.value = page
  } catch (error) {
    ElMessage.error(error instanceof Error ? error.message : '无人机设备加载失败')
  } finally {
    loadingDrones.value = false
  }
}

async function loadDroneTasks(): Promise<void> {
  loadingDroneTasks.value = true
  try {
    droneTaskPage.value = await fetchDroneTasks({
      page: droneTaskFilters.page,
      pageSize: droneTaskFilters.pageSize,
      keyword: droneTaskFilters.keyword || undefined,
      status: droneTaskFilters.status,
    })
  } catch (error) {
    ElMessage.error(error instanceof Error ? error.message : '无人机任务加载失败')
  } finally {
    loadingDroneTasks.value = false
  }
}

async function loadDroneFlights(): Promise<void> {
  loadingDroneFlights.value = true
  try {
    droneFlightPage.value = await fetchDroneFlights({
      page: droneFlightFilters.page,
      pageSize: droneFlightFilters.pageSize,
      keyword: droneFlightFilters.keyword || undefined,
      status: droneFlightFilters.status,
      droneNo: droneFlightFilters.droneNo || undefined,
    })
  } catch (error) {
    ElMessage.error(error instanceof Error ? error.message : '飞行记录加载失败')
  } finally {
    loadingDroneFlights.value = false
  }
}

async function loadDroneFaults(): Promise<void> {
  loadingDroneFaults.value = true
  try {
    droneFaultPage.value = await fetchDroneFaults({
      page: droneFaultFilters.page,
      pageSize: droneFaultFilters.pageSize,
      droneId: droneFaultFilters.droneId || undefined,
      status: droneFaultFilters.status,
      level: droneFaultFilters.level || undefined,
    })
  } catch (error) {
    ElMessage.error(error instanceof Error ? error.message : '无人机故障加载失败')
  } finally {
    loadingDroneFaults.value = false
  }
}

async function loadDroneMaintenances(): Promise<void> {
  loadingDroneMaintenances.value = true
  try {
    droneMaintenancePage.value = await fetchDroneMaintenances({
      page: droneMaintenanceFilters.page,
      pageSize: droneMaintenanceFilters.pageSize,
      droneId: droneMaintenanceFilters.droneId || undefined,
      status: droneMaintenanceFilters.status,
      type: droneMaintenanceFilters.type,
    })
  } catch (error) {
    ElMessage.error(error instanceof Error ? error.message : '无人机维护加载失败')
  } finally {
    loadingDroneMaintenances.value = false
  }
}

async function searchDrones(): Promise<void> {
  if (isDroneDevicesCenter.value) droneDeviceFilters.page = 1
  if (isDroneTasksCenter.value) droneTaskFilters.page = 1
  if (isDroneFlightsCenter.value) droneFlightFilters.page = 1
  if (isDroneFaultsCenter.value) droneFaultFilters.page = 1
  if (isDroneMaintenancesCenter.value) droneMaintenanceFilters.page = 1
  await refreshCurrentPage()
}

async function resetDroneFilters(): Promise<void> {
  if (isDroneDevicesCenter.value) {
    Object.assign(droneDeviceFilters, { page: 1, pageSize: 10, keyword: '', status: undefined, online: undefined })
  } else if (isDroneTasksCenter.value) {
    Object.assign(droneTaskFilters, { page: 1, pageSize: 10, keyword: '', status: undefined })
  } else if (isDroneFlightsCenter.value) {
    Object.assign(droneFlightFilters, { page: 1, pageSize: 10, keyword: '', status: undefined, droneNo: '' })
  } else if (isDroneFaultsCenter.value) {
    Object.assign(droneFaultFilters, { page: 1, pageSize: 10, droneId: '', status: undefined, level: '' })
  } else if (isDroneMaintenancesCenter.value) {
    Object.assign(droneMaintenanceFilters, { page: 1, pageSize: 10, droneId: '', status: undefined, type: undefined })
  }
  await refreshCurrentPage()
}

async function changeDronePage(page: number): Promise<void> {
  if (isDroneDevicesCenter.value) droneDeviceFilters.page = page
  if (isDroneTasksCenter.value) droneTaskFilters.page = page
  if (isDroneFlightsCenter.value) droneFlightFilters.page = page
  if (isDroneFaultsCenter.value) droneFaultFilters.page = page
  if (isDroneMaintenancesCenter.value) droneMaintenanceFilters.page = page
  await refreshCurrentPage()
}

function openCreateDrone(): void {
  Object.assign(droneForm, {
    id: undefined,
    droneNo: '',
    name: '',
    model: '',
    deviceType: '',
    warehouseCode: '',
    battery: 100,
    status: 'IDLE',
    remark: '',
  })
  droneStoreIds.value = []
  void ensureStoreOptions()
  droneEditorVisible.value = true
}

async function submitDrone(): Promise<void> {
  if (!droneForm.droneNo) {
    ElMessage.warning('请输入设备编号')
    return
  }
  droneSubmitting.value = true
  try {
    const payload = {
      id: droneForm.id,
      droneNo: droneForm.droneNo,
      name: droneForm.name || undefined,
      model: droneForm.model || undefined,
      deviceType: droneForm.deviceType || undefined,
      warehouseCode: droneForm.warehouseCode || undefined,
      storeId: droneStoreIds.value.join(',') || undefined,
      battery: droneForm.battery,
      status: droneForm.status,
      remark: droneForm.remark || undefined,
    }

    if (droneForm.id) {
      await updateDrone(payload)
      ElMessage.success('无人机设备更新成功')
    } else {
      await createDrone(payload)
      ElMessage.success('无人机设备创建成功')
    }

    droneEditorVisible.value = false
    droneDeviceFilters.page = 1
    await loadDroneDevices()
  } catch (error) {
    ElMessage.error(error instanceof Error ? error.message : '无人机设备保存失败')
  } finally {
    droneSubmitting.value = false
  }
}

function editDrone(drone: Awaited<ReturnType<typeof fetchDrones>>['list'][0]): void {
  Object.assign(droneForm, {
    id: drone.id,
    droneNo: drone.droneNo,
    name: drone.name || '',
    model: drone.model || '',
    deviceType: drone.deviceType || '',
    warehouseCode: drone.warehouseCode || '',
    battery: drone.battery ?? 100,
    status: drone.status,
    remark: drone.remark || '',
  })
  droneStoreIds.value = drone.storeId ? [drone.storeId] : []
  void ensureStoreOptions()
  droneEditorVisible.value = true
}

async function removeDrone(drone: Awaited<ReturnType<typeof fetchDrones>>['list'][0]): Promise<void> {
  try {
    await ElMessageBox.confirm(`确定要删除无人机设备 ${drone.droneNo} 吗？`, '删除确认', {
      type: 'warning',
    })
    deletingDroneId.value = drone.id
    try {
      await deleteDrone(drone.id)
      droneDeviceFilters.page = 1
      await loadDroneDevices()
      ElMessage.success('无人机设备删除成功')
    } catch (error) {
      ElMessage.error(error instanceof Error ? error.message : '无人机设备删除失败')
    } finally {
      deletingDroneId.value = ''
    }
  } catch {
    // 用户取消删除
  }
}

async function loadStores(): Promise<void> {
  loadingStores.value = true
  try {
    const stores = await fetchStores({
      keyword: isStoreCenter.value ? storeFilters.keywords.join(',') || undefined : undefined,
      status: isStoreCenter.value ? storeFilters.status || undefined : undefined,
    })
    if (isStoreCenter.value) storeRows.value = stores
    storeOptions.value = stores
  } catch (error) {
    ElMessage.error(error instanceof Error ? error.message : '门店列表加载失败')
  } finally {
    loadingStores.value = false
  }
}

async function loadProducts(): Promise<void> {
  if (!isProductList.value) return
  loadingProducts.value = true
  try {
    productPage.value = await fetchProducts({
      page: productFilters.page,
      pageSize: productFilters.pageSize,
      keyword: productFilters.keyword || undefined,
      categoryId: productFilters.categoryId || undefined,
      status: productFilters.status || undefined,
      storeId: productFilters.storeId || undefined,
    })
  } catch (error) {
    ElMessage.error(error instanceof Error ? error.message : '商品列表加载失败')
  } finally {
    loadingProducts.value = false
  }
}

async function loadCategories(): Promise<void> {
  if (!isProductCenter.value) return
  loadingCategories.value = true
  try {
    categories.value = await fetchCategories()
  } catch (error) {
    ElMessage.error(error instanceof Error ? error.message : '商品分类加载失败')
  } finally {
    loadingCategories.value = false
  }
}

async function loadCombos(): Promise<void> {
  if (!isComboCenter.value) return
  loadingCombos.value = true
  try {
    combos.value = await fetchCombos()
  } catch (error) {
    ElMessage.error(error instanceof Error ? error.message : '套餐列表加载失败')
  } finally {
    loadingCombos.value = false
  }
}

async function loadProductOptions(): Promise<void> {
  try {
    const page = await fetchProducts({ page: 1, pageSize: 100, status: 'ON' })
    productOptions.value = page.list
  } catch (error) {
    ElMessage.error(error instanceof Error ? error.message : '商品选项加载失败')
  }
}

async function loadUserOptions(): Promise<void> {
  try {
    const page = await fetchAdminUsers({ page: 1, pageSize: 100 })
    userOptions.value = page.list
  } catch (error) {
    ElMessage.error(error instanceof Error ? error.message : '用户选项加载失败')
  }
}

async function ensureEditorOptions(): Promise<void> {
  const tasks: Promise<void>[] = []
  if (!storeOptions.value.length) tasks.push(ensureStoreOptions())
  if (!productOptions.value.length) tasks.push(loadProductOptions())
  if (!extraRows.value.length) tasks.push(fetchExtras().then((extras) => { extraRows.value = extras }))
  if (!memberLevels.value.length) tasks.push(fetchMemberLevels().then((levels) => { memberLevels.value = levels }))
  await Promise.all(tasks)
}

async function ensureStoreOptions(): Promise<void> {
  if (storeOptions.value.length) return
  if (!canViewStoreList.value) {
    storeOptions.value = userStore.visibleStoreIds.map((id) => ({
      id,
      name: `授权门店 ${id}`,
      code: id,
      businessStatus: 'OPEN',
    }))
    return
  }
  await loadStores()
}

async function loadExtras(): Promise<void> {
  if (!isExtraCenter.value) return
  loadingExtras.value = true
  try {
    extraRows.value = await fetchExtras()
  } catch (error) {
    ElMessage.error(error instanceof Error ? error.message : '加料列表加载失败')
  } finally {
    loadingExtras.value = false
  }
}

async function loadInventory(): Promise<void> {
  if (!isInventoryCenter.value) return
  loadingInventory.value = true
  try {
    inventoryPage.value = await fetchInventory({
      page: inventoryFilters.page,
      pageSize: inventoryFilters.pageSize,
    })
    inventoryRows.value = inventoryPage.value.list
  } catch (error) {
    ElMessage.error(error instanceof Error ? error.message : '库存列表加载失败')
  } finally {
    loadingInventory.value = false
  }
}

async function loadMemberConfigs(): Promise<void> {
  if (!isMemberCenter.value) return
  loadingMembers.value = true
  try {
    if (isMemberLevelsCenter.value) {
      memberLevels.value = await fetchMemberLevels()
      return
    }
    if (isGrowthRulesCenter.value) {
      growthRules.value = await fetchGrowthRules()
      return
    }
    if (isPointsRulesCenter.value) {
      pointsRules.value = await fetchPointsRules()
      return
    }
    if (isMemberBenefitsCenter.value) {
      memberBenefits.value = await fetchMemberBenefits()
    }
  } catch (error) {
    ElMessage.error(error instanceof Error ? error.message : '会员配置加载失败')
  } finally {
    loadingMembers.value = false
  }
}

async function loadAdmins(): Promise<void> {
  if (!isSystemAdminCenter.value) return
  loadingAdmins.value = true
  try {
    adminPage.value = await fetchAdmins({
      page: adminFilters.page,
      pageSize: adminFilters.pageSize,
    })
    adminRows.value = adminPage.value.list
  } catch (error) {
    ElMessage.error(error instanceof Error ? error.message : '管理员列表加载失败')
  } finally {
    loadingAdmins.value = false
  }
}

async function loadRoles(): Promise<void> {
  if (!isSystemAdminCenter.value && !isRoleCenter.value) return
  loadingRoles.value = true
  try {
    systemRoles.value = await fetchRoles()
  } catch (error) {
    ElMessage.error(error instanceof Error ? error.message : '角色列表加载失败')
  } finally {
    loadingRoles.value = false
  }
}

async function loadOperationLogs(): Promise<void> {
  if (!isOperationLogsCenter.value) return
  loadingOperationLogs.value = true
  try {
    operationLogPage.value = await fetchOperationLogs({
      page: operationLogFilters.page,
      pageSize: operationLogFilters.pageSize,
    })
  } catch (error) {
    ElMessage.error(error instanceof Error ? error.message : '操作日志加载失败')
  } finally {
    loadingOperationLogs.value = false
  }
}

async function loadSystemParams(): Promise<void> {
  if (!isSystemParamsCenter.value) return
  loadingSystemParams.value = true
  try {
    systemParams.value = await fetchSystemParams()
  } catch (error) {
    ElMessage.error(error instanceof Error ? error.message : '系统参数加载失败')
  } finally {
    loadingSystemParams.value = false
  }
}

async function loadPaymentConfigs(): Promise<void> {
  if (!isPaymentConfigCenter.value) return
  loadingPaymentConfigs.value = true
  try {
    paymentConfigs.value = await fetchPaymentConfigs()
  } catch (error) {
    ElMessage.error(error instanceof Error ? error.message : '支付配置加载失败')
  } finally {
    loadingPaymentConfigs.value = false
  }
}

async function saveMemberConfigs(): Promise<void> {
  if (!isMemberCenter.value) return
  memberSubmitting.value = true
  try {
    if (isMemberLevelsCenter.value) {
      await updateMemberLevels(memberLevels.value)
    } else if (isGrowthRulesCenter.value) {
      await updateGrowthRules(growthRules.value)
    } else if (isPointsRulesCenter.value) {
      await updatePointsRules(pointsRules.value)
    } else if (isMemberBenefitsCenter.value) {
      await updateMemberBenefits(memberBenefits.value)
    }
    ElMessage.success('会员配置已保存')
    await loadMemberConfigs()
  } catch (error) {
    ElMessage.error(error instanceof Error ? error.message : '会员配置保存失败')
  } finally {
    memberSubmitting.value = false
  }
}

async function loadCoupons(): Promise<void> {
  if (!isCouponCenter.value) return
  loadingCoupons.value = true
  try {
    couponPage.value = await fetchCoupons({ page: couponFilters.page, pageSize: couponFilters.pageSize })
  } catch (error) {
    ElMessage.error(error instanceof Error ? error.message : '优惠券列表加载失败')
  } finally {
    loadingCoupons.value = false
  }
}

async function loadBanners(): Promise<void> {
  if (!isBannerCenter.value) return
  loadingBanners.value = true
  try {
    banners.value = await fetchBanners()
  } catch (error) {
    ElMessage.error(error instanceof Error ? error.message : '首页 Banner 加载失败')
  } finally {
    loadingBanners.value = false
  }
}

async function loadCouponOptions(): Promise<void> {
  try {
    const page = await fetchCoupons({ page: 1, pageSize: 100 })
    couponOptions.value = page.list
  } catch (error) {
    ElMessage.error(error instanceof Error ? error.message : '优惠券选项加载失败')
  }
}

async function loadRechargePackages(): Promise<void> {
  if (!isRechargeCenter.value) return
  loadingRechargePackages.value = true
  try {
    rechargePackages.value = await fetchRechargePackages()
  } catch (error) {
    ElMessage.error(error instanceof Error ? error.message : '充值套餐加载失败')
  } finally {
    loadingRechargePackages.value = false
  }
}

async function loadCardTemplates(): Promise<void> {
  if (!isCardCenter.value) return
  loadingCardTemplates.value = true
  try {
    cardTemplates.value = await fetchCardTemplates()
  } catch (error) {
    ElMessage.error(error instanceof Error ? error.message : '卡券模板加载失败')
  } finally {
    loadingCardTemplates.value = false
  }
}

async function loadGroupBuys(): Promise<void> {
  if (!isGroupBuyCenter.value) return
  loadingGroupBuys.value = true
  try {
    groupBuys.value = await fetchGroupBuys()
  } catch (error) {
    ElMessage.error(error instanceof Error ? error.message : '拼团活动加载失败')
  } finally {
    loadingGroupBuys.value = false
  }
}

async function loadBargains(): Promise<void> {
  if (!isBargainCenter.value) return
  loadingBargains.value = true
  try {
    bargains.value = await fetchBargains()
  } catch (error) {
    ElMessage.error(error instanceof Error ? error.message : '砍价活动加载失败')
  } finally {
    loadingBargains.value = false
  }
}

async function loadInviteConfigs(): Promise<void> {
  if (!isInviteCenter.value) return
  loadingInviteConfigs.value = true
  try {
    inviteConfigs.value = await fetchInviteConfigs()
  } catch (error) {
    ElMessage.error(error instanceof Error ? error.message : '邀请奖励配置加载失败')
  } finally {
    loadingInviteConfigs.value = false
  }
}

async function loadFlashSales(): Promise<void> {
  if (!isFlashSaleCenter.value) return
  loadingFlashSales.value = true
  try {
    flashSales.value = await fetchFlashSales()
  } catch (error) {
    ElMessage.error(error instanceof Error ? error.message : '秒杀活动加载失败')
  } finally {
    loadingFlashSales.value = false
  }
}

async function searchOrders(): Promise<void> { orderFilters.page = 1; await loadOrders() }
async function resetOrderFilters(): Promise<void> {
  orderFilters.orderNo = ''; orderFilters.phone = ''; orderFilters.storeId = ''; orderFilters.status = ''; orderFilters.deliveryType = ''; orderFilters.page = 1
  await loadOrders()
}
async function searchStores(): Promise<void> { await loadStores() }
async function resetStoreFilters(): Promise<void> { storeFilters.keywords = []; storeFilters.status = ''; await loadStores() }
async function changeOrderPage(page: number): Promise<void> { orderFilters.page = page; await loadOrders() }
async function changeRefundPage(page: number): Promise<void> { refundFilters.page = page; await loadRefunds() }
async function changeComplaintPage(page: number): Promise<void> {
  complaintPage.value = complaintPage.value ? { ...complaintPage.value, page } : undefined
  await loadComplaints()
}
async function searchProducts(): Promise<void> { productFilters.page = 1; await loadProducts() }
async function resetProductFilters(): Promise<void> {
  productFilters.keyword = ''
  productFilters.categoryId = ''
  productFilters.status = ''
  productFilters.storeId = userStore.isPlatformScope ? '' : (userStore.visibleStoreIds[0] ?? '')
  productFilters.page = 1
  await loadProducts()
}
async function changeProductPage(page: number): Promise<void> { productFilters.page = page; await loadProducts() }
async function changeInventoryPage(page: number): Promise<void> { inventoryFilters.page = page; await loadInventory() }
async function changeCouponPage(page: number): Promise<void> { couponFilters.page = page; await loadCoupons() }
async function changeAdminPage(page: number): Promise<void> { adminFilters.page = page; await loadAdmins() }
async function changeOperationLogPage(page: number): Promise<void> { operationLogFilters.page = page; await loadOperationLogs() }

async function viewUserOrders(user: AdminConsumerUser): Promise<void> {
  orderFilters.orderNo = ''
  orderFilters.phone = user.phone || ''
  orderFilters.storeId = ''
  orderFilters.status = ''
  orderFilters.deliveryType = ''
  orderFilters.page = 1
  await router.push('/orders')
  await loadOrders()
}

async function openOrderDetail(id: string): Promise<void> {
  try {
    selectedOrder.value = await fetchOrderDetail(id)
    statusForm.status = selectedOrder.value.status
    statusForm.remark = ''
    deliveryFeeReason.value = ''
    orderDetailVisible.value = true
  } catch (error) {
    ElMessage.error(error instanceof Error ? error.message : '订单详情加载失败')
  }
}

function openRefundAudit(refund: AdminRefund): void {
  selectedRefund.value = refund
  refundForm.result = 'APPROVED'
  refundForm.reason = ''
  refundAuditVisible.value = true
}

async function openStoreStats(store: AdminStore): Promise<void> {
  try {
    selectedStore.value = store
    selectedStoreStats.value = await fetchStoreStats(store.id)
    storeStatsVisible.value = true
  } catch (error) {
    ElMessage.error(error instanceof Error ? error.message : '门店统计加载失败')
  }
}

function openCreateStore(): void {
  Object.assign(storeForm, {
    id: undefined,
    name: '',
    imageUrl: '',
    category: '',
    categories: [],
    code: '',
    address: '',
    phone: '',
    longitude: undefined,
    latitude: undefined,
    businessStatus: 'OPEN',
    supportsDelivery: true,
    supportsDrone: false,
    openTime: '08:00',
    closeTime: '22:00',
  })
  storeEditorVisible.value = true
}

function openEditStore(store: AdminStore): void {
  Object.assign(storeForm, {
    id: store.id,
    name: store.name,
    imageUrl: store.imageUrl || '',
    category: store.category || '',
    categories: store.categories?.length ? [...store.categories] : parseDelimitedText(store.category || ''),
    code: store.code,
    address: store.address || '',
    phone: store.phone || '',
    longitude: store.longitude === undefined ? undefined : Number(store.longitude),
    latitude: store.latitude === undefined ? undefined : Number(store.latitude),
    businessStatus: store.businessStatus || 'OPEN',
    supportsDelivery: store.supportsDelivery ?? true,
    supportsDrone: store.supportsDrone ?? false,
    openTime: store.openTime || '08:00',
    closeTime: store.closeTime || '22:00',
  })
  storeEditorVisible.value = true
}

async function submitStore(): Promise<void> {
  if (!storeForm.name.trim()) {
    ElMessage.warning('请填写门店名称')
    return
  }
  storeSubmitting.value = true
  try {
    const payload: StoreSavePayload = {
      ...storeForm,
      name: storeForm.name.trim(),
      imageUrl: storeForm.imageUrl?.trim() || undefined,
      category: storeForm.categories?.join(',') || undefined,
      categories: storeForm.categories?.length ? [...storeForm.categories] : undefined,
      code: storeForm.code?.trim() || undefined,
      address: storeForm.address?.trim() || undefined,
      phone: storeForm.phone?.trim() || undefined,
      longitude: storeForm.longitude ? Number(storeForm.longitude) : undefined,
      latitude: storeForm.latitude ? Number(storeForm.latitude) : undefined,
      openTime: storeForm.openTime?.trim() || undefined,
      closeTime: storeForm.closeTime?.trim() || undefined,
    }
    if (isEditingStore.value) {
      await updateStore(payload)
      ElMessage.success('门店信息已更新')
    } else {
      delete payload.id
      await createStore(payload)
      ElMessage.success('门店已创建')
    }
    storeEditorVisible.value = false
    await loadStores()
  } catch (error) {
    ElMessage.error(error instanceof Error ? error.message : '门店保存失败')
  } finally {
    storeSubmitting.value = false
  }
}

async function removeStore(store: AdminStore): Promise<void> {
  try {
    await ElMessageBox.confirm(`确定删除门店“${store.name}”吗？删除后门店将不再出现在列表中。`, '删除门店', {
      type: 'warning',
      confirmButtonText: '确认删除',
      cancelButtonText: '取消',
    })
  } catch {
    return
  }
  deletingStoreId.value = store.id
  try {
    await deleteStore(store.id)
    ElMessage.success('门店已删除')
    await loadStores()
  } catch (error) {
    ElMessage.error(error instanceof Error ? error.message : '门店删除失败')
  } finally {
    deletingStoreId.value = ''
  }
}

async function openCreateProduct(): Promise<void> {
  await ensureEditorOptions()
  Object.assign(productForm, {
    id: undefined,
    storeId: productFilters.storeId || userStore.visibleStoreIds[0] || '',
    storeIds: undefined,
    name: '',
    categoryId: '',
    subtitle: '',
    description: '',
    mainImageUrl: '',
    originalPrice: undefined,
    status: 'ON',
    supportsDelivery: true,
    needSpec: true,
    stock: undefined,
    specGroups: createDefaultSpecGroups(),
  })
  productEditorVisible.value = true
}

async function openEditProduct(product: AdminProduct): Promise<void> {
  await ensureEditorOptions()
  Object.assign(productForm, {
    id: product.id,
    storeId: parseDelimitedText(product.storeIds || '')[0] || '',
    storeIds: product.storeIds || undefined,
    name: product.name,
    categoryId: product.categoryId,
    subtitle: product.subtitle || '',
    description: '',
    mainImageUrl: product.mainImageUrl,
    originalPrice: fenAmountToYuan(product.originalPrice),
    status: product.status || 'ON',
    supportsDelivery: product.supportsDelivery !== false,
    needSpec: product.needSpec !== false,
    stock: product.stock,
    specGroups: echoSpecGroupsForEditor(product.specGroups),
  })
  handleProductStoreChange()
  productStockOnOpen.value = product.stock
  productSkus.value = []
  try {
    // 多规格商品保存时要把商品级库存平摊到各规格，先取到规格列表
    productSkus.value = await fetchSkus(product.id)
  } catch {
    // 规格列表拿不到时按单规格处理：多规格商品的 stock 字段服务端会忽略，不会写错数据
  }
  productEditorVisible.value = true
}

function createDefaultSpecGroups(): ProductSpecGroup[] {
  return [
    {
      id: 'sugar',
      name: '可选糖',
      required: true,
      multiple: false,
      options: [{ id: 'default', name: '默认糖', priceInFen: 0, enabled: true }],
    },
    {
      id: 'temperature',
      name: '温度偏好',
      required: true,
      multiple: false,
      options: [{ id: 'ice', name: '少冰', priceInFen: 0, enabled: true }],
    },
    {
      id: 'topping',
      name: '精选小料',
      required: false,
      multiple: true,
      options: [],
    },
  ]
}

function cloneSpecGroups(groups?: ProductSpecGroup[]): ProductSpecGroup[] {
  return (groups ?? []).map((group) => ({
    ...group,
    options: (group.options ?? []).map((option) => ({ ...option })),
  }))
}

function appendProductSpecOption(groupIndex: number): void {
  const groups = cloneSpecGroups(productForm.specGroups)
  const group = groups[groupIndex]
  if (!group) return
  group.options.push({
    id: `option-${Date.now()}`,
    name: '新选项',
    priceInFen: 0,
    enabled: true,
  })
  productForm.specGroups = groups
}

function removeProductSpecOption(groupIndex: number, optionIndex: number): void {
  const groups = cloneSpecGroups(productForm.specGroups)
  const group = groups[groupIndex]
  if (!group) return
  group.options = group.options.filter((_, index) => index !== optionIndex)
  productForm.specGroups = groups
}

function setProductToppingOptions(extraIds: string[]): void {
  const groups = cloneSpecGroups(productForm.specGroups)
  const group = groups.find((item) => item.id === 'topping')
  if (!group) return
  group.options = extraIds
    .map((extraId) => extraRows.value.find((extra) => extra.id === extraId))
    .filter((extra): extra is AdminExtra => Boolean(extra))
    .map((extra) => ({
      id: extra.id,
      name: extra.name,
      priceInFen: fenAmountToYuan(extra.price) ?? 0,
      enabled: extra.status !== 'DISABLED',
      recommended: extra.recommended,
      sort: extra.sort,
    }))
  productForm.specGroups = groups
}

function handleProductStoreChange(): void {
  const category = categories.value.find((item) => item.id === productForm.categoryId)
  if (!category) return
  const storeIds = category.storeIds?.length
    ? category.storeIds.map(String).map((item) => item.trim()).filter(Boolean)
    : parseDelimitedText(category.storeId || '')
  if (storeIds.length && !storeIds.includes(productForm.storeId || '')) productForm.categoryId = ''
}

async function submitProduct(): Promise<void> {
  if (!productForm.name.trim() || !productForm.categoryId || !productForm.storeId || !productForm.mainImageUrl.trim()) {
    ElMessage.warning('请补齐商品名称、分类、所属门店和主图')
    return
  }
  productSubmitting.value = true
  try {
    const payload: ProductSavePayload = {
      ...productForm,
      name: productForm.name.trim(),
      storeIds: JSON.stringify([productForm.storeId]),
      mainImageUrl: productForm.mainImageUrl.trim(),
      subtitle: productForm.subtitle?.trim() || undefined,
      description: productForm.description?.trim() || undefined,
      originalPrice: yuanAmountToFen(productForm.originalPrice),
      specGroups: normalizeSpecGroupsForSave(productForm.specGroups),
    }
    if (isEditingProduct.value) {
      applyProductSkuStocks(payload)
      await updateProduct(payload)
      ElMessage.success('商品信息已更新')
    } else {
      delete payload.id
      await createProduct(payload)
      ElMessage.success('商品已创建')
    }
    productEditorVisible.value = false
    await loadProducts()
  } catch (error) {
    ElMessage.error(error instanceof Error ? error.message : '商品保存失败')
  } finally {
    productSubmitting.value = false
  }
}

/**
 * 多规格商品的弹窗只有一个库存框：改过库存就把这个数字平摊到各规格（列表显示的合计因此等于输入值），
 * 没改过则不碰库存，避免把各规格已有的分布重刷成平均值。门店范围由服务端按商品门店范围展开。
 */
function applyProductSkuStocks(payload: ProductSavePayload): void {
  if (productSkus.value.length < 2 || !productStockChanged()) return
  const parts = splitStockAcrossSkus(productForm.stock, productSkus.value.length)
  payload.skuStocks = productSkus.value.map((sku, index) => ({
    skuId: sku.id,
    stock: parts[index] ?? 0,
  }))
  payload.stock = undefined
}

/** 是否真的改过库存：清空或非法值视为没改，避免误把各规格清零。 */
function productStockChanged(): boolean {
  const current = Number(productForm.stock)
  if (!Number.isFinite(current)) return false
  const onOpen = Number(productStockOnOpen.value)
  return !Number.isFinite(onOpen) || Math.trunc(current) !== Math.trunc(onOpen)
}

async function removeProduct(product: AdminProduct): Promise<void> {
  await confirmRemoveEntity('商品', product.name, async () => {
    await deleteProduct(product.id)
    await loadProducts()
  })
}

function applyDefaultProductStore(): void {
  if (!productFilters.storeId && !userStore.isPlatformScope && userStore.visibleStoreIds.length) {
    productFilters.storeId = userStore.visibleStoreIds[0]
  }
}

async function openProductListing(product: AdminProduct): Promise<void> {
  await ensureStoreOptions()
  applyDefaultProductStore()
  const storeId = productFilters.storeId
  if (!storeId) {
    ElMessage.warning('请先选择门店视角')
    return
  }
  selectedListingProduct.value = product
  productListing.value = undefined
  productListingVisible.value = true
  productListingLoading.value = true
  try {
    productListing.value = await fetchProductListing(product.id, storeId)
  } catch (error) {
    productListingVisible.value = false
    ElMessage.error(error instanceof Error ? error.message : '门店商品配置加载失败')
  } finally {
    productListingLoading.value = false
  }
}

async function submitProductListing(payload: ProductListingSavePayload): Promise<void> {
  const product = selectedListingProduct.value
  if (!product) return
  productListingSubmitting.value = true
  try {
    await saveProductListing(product.id, payload)
    productListingVisible.value = false
    ElMessage.success('门店商品配置已保存')
    await loadProducts()
  } catch (error) {
    ElMessage.error(error instanceof Error ? error.message : '门店商品配置保存失败')
  } finally {
    productListingSubmitting.value = false
  }
}

async function removeCurrentProductListing(): Promise<void> {
  const product = selectedListingProduct.value
  const storeId = productListing.value?.storeId
  if (!product || !storeId) return
  try {
    await ElMessageBox.confirm(`确定在当前门店下架“${product.name}”吗？`, '门店下架', { type: 'warning' })
  } catch {
    return
  }
  productListingSubmitting.value = true
  try {
    await removeProductListing(product.id, storeId)
    productListingVisible.value = false
    ElMessage.success('已在当前门店下架')
    await loadProducts()
  } catch (error) {
    ElMessage.error(error instanceof Error ? error.message : '门店下架失败')
  } finally {
    productListingSubmitting.value = false
  }
}

async function openComboEditor(combo?: AdminCombo): Promise<void> {
  await ensureEditorOptions()
  Object.assign(comboForm, {
    id: combo?.id,
    storeId: combo?.storeId || '',
    name: combo?.name || '',
    originalPrice: fenAmountToYuan(combo?.originalPrice) ?? 0,
    salePrice: fenAmountToYuan(combo?.salePrice) ?? 0,
    status: combo?.status || 'ENABLED',
    items: combo?.items ? combo.items.map((item) => ({ ...item })) : [],
  })
  comboStoreIds.value = parseDelimitedText(combo?.storeId || '')
  if (!combo && !userStore.isPlatformScope && !comboStoreIds.value.length) {
    comboStoreIds.value = [...userStore.visibleStoreIds]
  }
  Object.assign(comboItemForm, {
    productId: '',
    productName: '',
    quantity: 1,
  })
  comboEditorVisible.value = true
}

function appendComboItem(): void {
  const product = productOptions.value.find((item) => item.id === comboItemForm.productId)
  if (!product) {
    ElMessage.warning('请选择商品')
    return
  }
  const quantity = Number(comboItemForm.quantity ?? 0)
  if (!Number.isInteger(quantity) || quantity <= 0) {
    ElMessage.warning('商品数量必须是大于 0 的整数')
    return
  }
  comboForm.items = [
    ...(comboForm.items ?? []),
    {
      productId: product.id,
      productName: product.name,
      quantity,
    },
  ]
  Object.assign(comboItemForm, {
    productId: '',
    productName: '',
    quantity: 1,
  })
}

function removeComboItem(index: number): void {
  comboForm.items = (comboForm.items ?? []).filter((_, itemIndex) => itemIndex !== index)
}

async function submitCombo(): Promise<void> {
  if (!comboForm.name?.trim()) {
    ElMessage.warning('请填写套餐名称')
    return
  }
  if (!comboForm.items?.length) {
    ElMessage.warning('请至少添加一项套餐商品')
    return
  }
  if (!userStore.isPlatformScope && !comboStoreIds.value.length) {
    ElMessage.warning('请选择适用门店')
    return
  }
  comboSubmitting.value = true
  try {
    const payload: ComboSavePayload = {
      ...comboForm,
      name: comboForm.name.trim(),
      storeId: comboStoreIds.value.join(',') || undefined,
      originalPrice: yuanAmountToFen(comboForm.originalPrice),
      salePrice: yuanAmountToFen(comboForm.salePrice),
      items: comboForm.items,
    }
    if (isEditingCombo.value && comboForm.id) {
      await updateCombo(comboForm.id, payload)
      ElMessage.success('商品套餐已更新')
    } else {
      delete payload.id
      await createCombo(payload)
      ElMessage.success('商品套餐已创建')
    }
    comboEditorVisible.value = false
    await loadCombos()
  } catch (error) {
    ElMessage.error(error instanceof Error ? error.message : '商品套餐保存失败')
  } finally {
    comboSubmitting.value = false
  }
}

async function removeCombo(combo: AdminCombo): Promise<void> {
  await confirmRemoveEntity('商品套餐', combo.name, async () => {
    await deleteCombo(combo.id)
    await loadCombos()
  })
}

async function openCreateCategory(): Promise<void> {
  await ensureEditorOptions()
  Object.assign(categoryForm, {
    id: undefined,
    storeId: '',
    storeIds: [],
    name: '',
    parentId: undefined,
    tab: MenuTab.Classic,
    iconUrl: undefined,
    sort: 0,
    status: 'ENABLED',
    supportsDelivery: true,
  })
  categoryStoreIds.value = []
  categoryEditorVisible.value = true
}

async function openEditCategory(category: AdminCategory): Promise<void> {
  await ensureEditorOptions()
  Object.assign(categoryForm, {
    id: category.id,
    storeId: category.storeId || '',
    storeIds: category.storeIds ? [...category.storeIds] : [],
    name: category.name,
    parentId: category.parentId,
    tab: MenuTab.Classic,
    iconUrl: category.iconUrl,
    sort: category.sort ?? 0,
    status: category.status || 'ENABLED',
    supportsDelivery: category.supportsDelivery !== false,
  })
  categoryStoreIds.value = category.storeIds?.length ? [...category.storeIds] : parseDelimitedText(category.storeId || '')
  categoryEditorVisible.value = true
}

async function submitCategory(): Promise<void> {
  if (!categoryForm.name.trim()) {
    ElMessage.warning('请填写分类名称')
    return
  }
  categorySubmitting.value = true
  try {
    const payload: CategorySavePayload = {
      ...categoryForm,
      name: categoryForm.name.trim(),
      storeId: categoryStoreIds.value.join(',') || undefined,
      storeIds: categoryStoreIds.value.length ? [...categoryStoreIds.value] : undefined,
      tab: MenuTab.Classic,
    }
    if (isEditingCategory.value) {
      await updateCategory(categoryForm.id as string, payload)
      ElMessage.success('分类已更新')
    } else {
      delete payload.id
      await createCategory(payload)
      ElMessage.success('分类已创建')
    }
    categoryEditorVisible.value = false
    await loadCategories()
    if (isProductList.value) await loadProducts()
  } catch (error) {
    ElMessage.error(error instanceof Error ? error.message : '分类保存失败')
  } finally {
    categorySubmitting.value = false
  }
}

async function removeCategory(category: AdminCategory): Promise<void> {
  await confirmRemoveEntity('分类', category.name, async () => {
    await deleteCategory(category.id)
    await loadCategories()
    if (isProductList.value) await loadProducts()
  })
}

function openCreateExtra(): void {
  Object.assign(extraForm, {
    id: undefined,
    name: '',
    storeId: userStore.isPlatformScope ? undefined : userStore.visibleStoreIds[0],
    price: 0,
    recommended: false,
    enabled: true,
    sort: 0,
  })
  extraEditorVisible.value = true
}

function openEditExtra(extra: AdminExtra): void {
  Object.assign(extraForm, {
    id: extra.id,
    name: extra.name,
    storeId: extra.storeId,
    price: fenAmountToYuan(extra.price) ?? 0,
    recommended: extra.recommended ?? false,
    enabled: extra.status === 'ENABLED',
    sort: extra.sort ?? 0,
  })
  extraEditorVisible.value = true
}

async function submitExtra(): Promise<void> {
  if (!extraForm.name.trim()) {
    ElMessage.warning('请填写加料名称')
    return
  }
  if (!userStore.isPlatformScope && !extraForm.storeId) {
    ElMessage.warning('请选择适用门店')
    return
  }
  extraSubmitting.value = true
  try {
    const payload: ExtraSavePayload = {
      ...extraForm,
      name: extraForm.name.trim(),
      price: yuanAmountToFen(extraForm.price),
    }
    if (isEditingExtra.value) {
      await updateExtra(extraForm.id as string, payload)
      ElMessage.success('加料已更新')
    } else {
      delete payload.id
      await createExtra(payload)
      ElMessage.success('加料已创建')
    }
    extraEditorVisible.value = false
    await loadExtras()
  } catch (error) {
    ElMessage.error(error instanceof Error ? error.message : '加料保存失败')
  } finally {
    extraSubmitting.value = false
  }
}

async function removeExtra(extra: AdminExtra): Promise<void> {
  await confirmRemoveEntity('加料', extra.name, async () => {
    await deleteExtra(extra.id)
    await loadExtras()
  })
}

async function confirmRemoveEntity(
  entityName: string,
  displayName: string,
  action: () => Promise<void>,
): Promise<void> {
  try {
    await ElMessageBox.confirm(`确定删除${entityName}“${displayName}”吗？删除后不可恢复。`, `删除${entityName}`, {
      type: 'warning',
      confirmButtonText: '确认删除',
      cancelButtonText: '取消',
      confirmButtonClass: 'el-button--danger',
    })
  } catch {
    return
  }
  try {
    await action()
    ElMessage.success(`${entityName}已删除`)
  } catch (error) {
    ElMessage.error(error instanceof Error ? error.message : `${entityName}删除失败`)
  }
}

async function openInventoryEditor(inventory?: AdminInventory): Promise<void> {
  await ensureEditorOptions()
  Object.assign(inventoryForm, {
    skuId: inventory?.skuId || '',
    storeId: inventory?.storeId || '',
    stock: inventory?.stock ?? undefined,
    currentStock: inventory?.stock,
  })
  inventoryStoreIds.value = parseDelimitedText(inventory?.storeId || '')
  if (!inventory && !userStore.isPlatformScope && !inventoryStoreIds.value.length) {
    inventoryStoreIds.value = [...userStore.visibleStoreIds]
  }
  inventoryEditorVisible.value = true
}

async function submitInventory(): Promise<void> {
  if (!inventoryForm.skuId || inventoryForm.stock === undefined) {
    ElMessage.warning('请填写 SKU 和库存')
    return
  }
  if (!userStore.isPlatformScope && !inventoryStoreIds.value.length) {
    ElMessage.warning('请选择适用门店')
    return
  }
  inventorySubmitting.value = true
  try {
    await updateInventory({
      skuId: inventoryForm.skuId.trim(),
      storeId: inventoryStoreIds.value.join(',') || undefined,
      stock: inventoryForm.stock,
    })
    ElMessage.success('库存已更新')
    inventoryEditorVisible.value = false
    await loadInventory()
  } catch (error) {
    ElMessage.error(error instanceof Error ? error.message : '库存更新失败')
  } finally {
    inventorySubmitting.value = false
  }
}

function openMemberLevelEditor(item?: MemberLevelConfig): void {
  Object.assign(memberLevelForm, {
    id: item?.id,
    code: item?.code || '',
    name: item?.name || '',
    growthThreshold: item?.growthThreshold ?? 0,
    discountRate: item?.discountRate ?? 1,
    pointsMultiplier: item?.pointsMultiplier ?? 1,
    badge: item?.badge || '',
    sort: item?.sort ?? 0,
  })
  memberLevelEditorVisible.value = true
}

async function submitMemberLevelEditor(): Promise<void> {
  if (!memberLevelForm.code.trim()) {
    ElMessage.warning('请填写等级编码')
    return
  }
  if (!memberLevelForm.name?.trim()) {
    ElMessage.warning('请填写等级名称')
    return
  }
  const payload: MemberLevelSavePayload = {
    code: memberLevelForm.code.trim(),
    name: memberLevelForm.name.trim(),
    growthThreshold: memberLevelForm.growthThreshold,
    discountRate: memberLevelForm.discountRate,
    pointsMultiplier: memberLevelForm.pointsMultiplier,
    badge: memberLevelForm.badge?.trim() || undefined,
    sort: memberLevelForm.sort,
  }
  if (isEditingMemberLevel.value) {
    if (!memberLevelForm.id) return
    memberSubmitting.value = true
    try {
      await updateMemberLevel(memberLevelForm.id, payload)
      ElMessage.success('会员等级已更新')
      memberLevelEditorVisible.value = false
      await loadMemberConfigs()
    } catch (error) {
      ElMessage.error(error instanceof Error ? error.message : '会员等级更新失败')
    } finally {
      memberSubmitting.value = false
    }
    return
  }
  memberSubmitting.value = true
  try {
    await createMemberLevel(payload)
    ElMessage.success('会员等级已新增')
    memberLevelEditorVisible.value = false
    await loadMemberConfigs()
  } catch (error) {
    ElMessage.error(error instanceof Error ? error.message : '会员等级新增失败')
  } finally {
    memberSubmitting.value = false
  }
}

function openGrowthRuleEditor(item?: GrowthRuleConfig): void {
  Object.assign(growthRuleForm, {
    id: item?.id,
    ruleType: '消费',
    param: item?.param || '',
    enabled: item?.enabled ?? true,
    growthValuePerYuan: getGrowthValuePerYuan(item?.param),
  })
  growthRuleEditorVisible.value = true
}

function submitGrowthRuleEditor(): void {
  const growthValuePerYuan = Number(growthRuleForm.growthValuePerYuan ?? 0)
  if (!Number.isFinite(growthValuePerYuan) || growthValuePerYuan <= 0) {
    ElMessage.warning('请填写每消费 1 元获得多少成长值')
    return
  }
  const payload: GrowthRuleConfig = {
    ruleType: '消费',
    param: `每消费 1 元得 ${growthValuePerYuan} 成长值`,
    enabled: growthRuleForm.enabled,
  }
  if (growthRuleForm.id) payload.id = growthRuleForm.id
  if (isEditingGrowthRule.value) {
    const index = growthRules.value.findIndex((item) => item.id === growthRuleForm.id)
    if (index >= 0) growthRules.value[index] = payload
  } else {
    growthRules.value.push(payload)
  }
  growthRuleEditorVisible.value = false
}

function openPointsRuleEditor(item?: PointsRuleConfig): void {
  Object.assign(pointsRuleForm, {
    id: item?.id,
    ruleType: '消费',
    calcMethod: item?.calcMethod || '',
    value: item?.value ?? 1,
    maxUsePerOrder: item?.maxUsePerOrder ?? 0,
    enabled: item?.enabled ?? true,
  })
  pointsRuleEditorVisible.value = true
}

function submitPointsRuleEditor(): void {
  const pointsPerYuan = Number(pointsRuleForm.value ?? 0)
  if (!Number.isFinite(pointsPerYuan) || pointsPerYuan <= 0) {
    ElMessage.warning('请填写每消费 1 元获得多少积分')
    return
  }
  const payload: PointsRuleConfig = {
    ruleType: '消费',
    calcMethod: `每消费 1 元得 ${pointsPerYuan} 积分`,
    value: pointsPerYuan,
    maxUsePerOrder: pointsRuleForm.maxUsePerOrder ?? 0,
    enabled: pointsRuleForm.enabled,
  }
  if (pointsRuleForm.id) payload.id = pointsRuleForm.id
  if (isEditingPointsRule.value) {
    const index = pointsRules.value.findIndex((item) => item.id === pointsRuleForm.id)
    if (index >= 0) pointsRules.value[index] = payload
  } else {
    pointsRules.value.push(payload)
  }
  pointsRuleEditorVisible.value = false
}

async function openMemberBenefitEditor(item?: MemberBenefitConfig): Promise<void> {
  await ensureEditorOptions()
  Object.assign(memberBenefitForm, {
    id: item?.id,
    levelCode: item?.levelCode || '',
    benefitType: item?.benefitType || '',
    config: item?.config || '',
    enabled: item?.enabled ?? true,
    sort: item?.sort ?? 0,
  })
  memberBenefitEditorVisible.value = true
}

function submitMemberBenefitEditor(): void {
  if (!memberBenefitForm.levelCode.trim()) {
    ElMessage.warning('请填写适用等级编码')
    return
  }
  const payload: MemberBenefitConfig = {
    ...memberBenefitForm,
    levelCode: memberBenefitForm.levelCode.trim(),
    benefitType: memberBenefitForm.benefitType?.trim() || undefined,
    config: memberBenefitForm.config?.trim() || undefined,
  }
  if (isEditingMemberBenefit.value) {
    const index = memberBenefits.value.findIndex((item) => item.id === memberBenefitForm.id)
    if (index >= 0) memberBenefits.value[index] = payload
  } else {
    memberBenefits.value.push(payload)
  }
  memberBenefitEditorVisible.value = false
}

async function removeMemberConfigItem<T>(
  items: Ref<T[]>,
  target: T,
  getKey: (item: T) => string,
  save: (payload: T[]) => Promise<void>,
  name: string,
): Promise<void> {
  try {
    await ElMessageBox.confirm(`确定要删除${name}吗？`, '删除确认', { type: 'warning' })
  } catch {
    return
  }
  const previous = [...items.value]
  items.value = items.value.filter((item) => getKey(item) !== getKey(target))
  memberSubmitting.value = true
  try {
    await save(items.value)
    ElMessage.success(`${name}已删除`)
    await loadMemberConfigs()
  } catch (error) {
    items.value = previous
    ElMessage.error(error instanceof Error ? error.message : `${name}删除失败`)
  } finally {
    memberSubmitting.value = false
  }
}

async function removeMemberLevel(item: MemberLevelConfig): Promise<void> {
  if (!item.id) {
    memberLevels.value = memberLevels.value.filter((level) => level.code !== item.code)
    return
  }
  try {
    await ElMessageBox.confirm(`确定要删除会员等级“${item.name || item.code}”吗？删除后不可恢复。`, '删除会员等级', { type: 'warning' })
  } catch {
    return
  }
  memberSubmitting.value = true
  try {
    await deleteMemberLevel(item.id)
    ElMessage.success('会员等级已删除')
    await loadMemberConfigs()
  } catch (error) {
    ElMessage.error(error instanceof Error ? error.message : '会员等级删除失败')
  } finally {
    memberSubmitting.value = false
  }
}

async function removeGrowthRule(item: GrowthRuleConfig): Promise<void> {
  await removeMemberConfigItem(growthRules, item, (rule) => rule.id || rule.ruleType, updateGrowthRules, '成长值规则')
}

async function removePointsRule(item: PointsRuleConfig): Promise<void> {
  await removeMemberConfigItem(pointsRules, item, (rule) => rule.id || rule.ruleType, updatePointsRules, '积分规则')
}

async function removeMemberBenefit(item: MemberBenefitConfig): Promise<void> {
  await removeMemberConfigItem(memberBenefits, item, (benefit) => benefit.id || `${benefit.levelCode}-${benefit.benefitType || ''}`, updateMemberBenefits, '会员权益')
}

async function openAdminEditor(item?: AdminAccount): Promise<void> {
  await ensureEditorOptions()
  const roleIds = item?.roles
    ?.map((roleName) => systemRoles.value.find((role) => role.name === roleName)?.id)
    .filter((roleId): roleId is string => Boolean(roleId)) ?? []
  Object.assign(adminForm, {
    id: item?.id,
    account: item?.username || '',
    password: '',
    nickname: item?.nickname || '',
    roleIds,
    storeIds: item?.storeIds ? [...item.storeIds] : [],
    status: item?.status || 'ENABLED',
  })
  adminRoleIdsTouched.value = false
  adminEditorVisible.value = true
}

function openRoleEditor(item?: AdminRole): void {
  Object.assign(roleForm, {
    id: item?.id,
    name: item?.name || '',
    code: item?.code || '',
    dataScope: item?.dataScope || 'ALL',
    permissions: expandImplicitPermissions(item?.permissions ?? []),
  })
  roleEditorVisible.value = true
}

async function submitAdmin(): Promise<void> {
  const account = adminForm.account.trim()
  const password = adminForm.password?.trim() || ''
  if (!account) {
    ElMessage.warning('请填写登录账号')
    return
  }
  if (!isEditingAdmin.value && !password) {
    ElMessage.warning('新建管理员必须填写登录密码')
    return
  }
  const selectedRoles = (adminForm.roleIds ?? [])
    .map((roleId) => systemRoles.value.find((role) => role.id === roleId))
    .filter((role): role is AdminRole => Boolean(role))
  if (selectedRoles.some((role) => role.dataScope === 'STORE') && !(adminForm.storeIds ?? []).length) {
    ElMessage.warning('所属门店数据范围的角色必须至少绑定一家门店')
    return
  }
  adminSubmitting.value = true
  try {
    const payload: AdminSavePayload = {
      id: adminForm.id,
      account,
      password: password || undefined,
      nickname: adminForm.nickname?.trim() || '',
      roleIds: isEditingAdmin.value && !adminRoleIdsTouched.value ? undefined : [...(adminForm.roleIds ?? [])],
      storeIds: [...(adminForm.storeIds ?? [])],
      status: adminForm.status || 'ENABLED',
    }
    if (isEditingAdmin.value) {
      await updateAdmin(payload)
      ElMessage.success('管理员信息已更新')
    } else {
      delete payload.id
      await createAdmin(payload)
      ElMessage.success('管理员已创建')
    }
    adminEditorVisible.value = false
    await loadAdmins()
  } catch (error) {
    ElMessage.error(error instanceof Error ? error.message : '管理员保存失败')
  } finally {
    adminSubmitting.value = false
  }
}

async function removeAdmin(item: AdminAccount): Promise<void> {
  await confirmRemoveEntity('管理员', item.nickname || item.username, async () => {
    await deleteAdmin(item.id)
    await loadAdmins()
  })
}

async function submitRole(): Promise<void> {
  const name = roleForm.name.trim()
  if (!name) {
    ElMessage.warning('请填写角色名称')
    return
  }
  roleSubmitting.value = true
  try {
    const permissions = expandImplicitPermissions(roleForm.permissions ?? [])
    const payload: RoleSavePayload = {
      id: roleForm.id,
      name,
      code: roleForm.code?.trim() || undefined,
      dataScope: roleForm.dataScope || 'ALL',
      permissions,
    }
    if (isEditingRole.value) {
      await updateRole(payload)
      ElMessage.success('角色信息已更新')
    } else {
      delete payload.id
      await createRole(payload)
      ElMessage.success('角色已创建')
    }
    roleEditorVisible.value = false
    await loadRoles()
  } catch (error) {
    ElMessage.error(error instanceof Error ? error.message : '角色保存失败')
  } finally {
    roleSubmitting.value = false
  }
}

async function removeRole(item: AdminRole): Promise<void> {
  await confirmRemoveEntity('角色', item.name, async () => {
    await deleteRole(item.id)
    await loadRoles()
  })
}

function openSystemParamEditor(item?: SystemParam): void {
  Object.assign(systemParamForm, {
    id: item?.id,
    key: item?.key || '',
    value: item?.value || '',
    groupName: item?.groupName || 'GENERAL',
    remark: item?.remark || '',
  })
  systemParamEditorVisible.value = true
}

function openPaymentConfigEditor(item?: PaymentConfig): void {
  Object.assign(paymentConfigForm, {
    id: item?.id,
    channel: item?.channel || '',
    appId: '',
    mchId: '',
    apiKey: '',
    apiV3Key: '',
    notifyUrl: item?.notifyUrl || '',
    enabled: item?.enabled ?? true,
  })
  paymentConfigEditorVisible.value = true
}

async function submitSystemParam(): Promise<void> {
  const key = systemParamForm.key.trim()
  if (!key) {
    ElMessage.warning('请填写参数编码')
    return
  }
  systemParamsSubmitting.value = true
  try {
    const payload: SystemParamSavePayload = {
      id: systemParamForm.id,
      key,
      value: systemParamForm.value?.trim() || '',
      groupName: systemParamForm.groupName?.trim() || undefined,
      remark: systemParamForm.remark?.trim() || undefined,
    }
    await updateSystemParams([payload])
    ElMessage.success(isEditingSystemParam.value ? '系统参数已更新' : '系统参数已创建')
    systemParamEditorVisible.value = false
    await loadSystemParams()
  } catch (error) {
    ElMessage.error(error instanceof Error ? error.message : '系统参数保存失败')
  } finally {
    systemParamsSubmitting.value = false
  }
}

async function removeSystemParam(item: SystemParam): Promise<void> {
  await confirmRemoveEntity('系统参数', item.key, async () => {
    await deleteSystemParam(item.id)
    await loadSystemParams()
  })
}

async function submitPaymentConfig(): Promise<void> {
  const channel = paymentConfigForm.channel.trim()
  if (!channel) {
    ElMessage.warning('请填写支付渠道编码')
    return
  }
  paymentConfigSubmitting.value = true
  try {
    const payload: PaymentConfigSavePayload = {
      id: paymentConfigForm.id,
      channel,
      appId: paymentConfigForm.appId?.trim() || undefined,
      mchId: paymentConfigForm.mchId?.trim() || undefined,
      apiKey: paymentConfigForm.apiKey?.trim() || undefined,
      apiV3Key: paymentConfigForm.apiV3Key?.trim() || undefined,
      notifyUrl: paymentConfigForm.notifyUrl?.trim() || undefined,
      enabled: paymentConfigForm.enabled ?? true,
    }
    await updatePaymentConfig(payload)
    ElMessage.success(isEditingPaymentConfig.value ? '支付配置已更新' : '支付配置已创建')
    paymentConfigEditorVisible.value = false
    await loadPaymentConfigs()
  } catch (error) {
    ElMessage.error(error instanceof Error ? error.message : '支付配置保存失败')
  } finally {
    paymentConfigSubmitting.value = false
  }
}

function openCouponEditor(item?: AdminCoupon): void {
  Object.assign(couponForm, {
    id: item?.id,
    name: item?.name || '',
    type: item?.type || 'FULL_REDUCTION',
    amount: fenAmountToYuan(item?.amount) ?? 0,
    discountRate: item?.discountRate,
    threshold: fenAmountToYuan(item?.threshold) ?? 0,
    validType: item?.validType || 'DATE_RANGE',
    validFrom: item?.validFrom || '',
    validTo: item?.validTo || '',
    validDays: item?.validDays,
    totalCount: item?.totalCount ?? 0,
    perUserLimit: item?.perUserLimit ?? 1,
    storeIds: item?.storeIds || '',
    productScope: item?.productScope || 'ALL',
    status: item?.status || 'ENABLED',
  })
  couponStoreIds.value = parseDelimitedText(item?.storeIds || '')
  if (!item && !userStore.isPlatformScope && !couponStoreIds.value.length) {
    couponStoreIds.value = [...userStore.visibleStoreIds]
  }
  couponEditorVisible.value = true
}

async function openCouponIssue(item: AdminCoupon): Promise<void> {
  if (!userOptions.value.length) await loadUserOptions()
  couponIssueForm.couponId = item.id
  couponIssueForm.targetType = 'ALL'
  couponIssueForm.targetIds = []
  couponIssueForm.count = 1
  couponIssueTargetUserIds.value = []
  couponIssueVisible.value = true
}

async function submitCoupon(): Promise<void> {
  if (!couponForm.name?.trim()) {
    ElMessage.warning('请填写券名称')
    return
  }
  if (!userStore.isPlatformScope && !couponStoreIds.value.length) {
    ElMessage.warning('请选择适用门店')
    return
  }
  couponSubmitting.value = true
  try {
    const payload: CouponSavePayload = {
      ...couponForm,
      name: couponForm.name.trim(),
      validFrom: couponForm.validFrom?.trim() || undefined,
      validTo: couponForm.validTo?.trim() || undefined,
      storeIds: couponStoreIds.value.length ? JSON.stringify(couponStoreIds.value) : undefined,
      productScope: couponForm.productScope?.trim() || undefined,
      amount: couponForm.type === 'DISCOUNT' ? undefined : yuanAmountToFen(couponForm.amount),
      discountRate: couponForm.type === 'DISCOUNT' ? couponForm.discountRate : undefined,
      threshold: yuanAmountToFen(couponForm.threshold),
    }
    if (isEditingCoupon.value) {
      await updateCoupon(payload)
      ElMessage.success('优惠券已更新')
    } else {
      delete payload.id
      await createCoupon(payload)
      ElMessage.success('优惠券已创建')
    }
    couponEditorVisible.value = false
    await loadCoupons()
  } catch (error) {
    ElMessage.error(error instanceof Error ? error.message : '优惠券保存失败')
  } finally {
    couponSubmitting.value = false
  }
}

async function removeCoupon(coupon: AdminCoupon): Promise<void> {
  await confirmRemoveEntity('优惠券', coupon.name, async () => {
    await deleteCoupon(coupon.id)
    await loadCoupons()
  })
}

async function removeBanner(banner: AdminBanner): Promise<void> {
  await confirmRemoveEntity('首页 Banner', banner.title, async () => {
    await deleteBanner(banner.id)
    await loadBanners()
  })
}

async function removeRechargePackage(item: AdminRechargePackage): Promise<void> {
  await confirmRemoveEntity('充值套餐', item.name, async () => {
    await deleteRechargePackage(item.id)
    await loadRechargePackages()
  })
}

async function removeCardTemplate(item: AdminCardTemplate): Promise<void> {
  await confirmRemoveEntity('卡券模板', item.name, async () => {
    await deleteCardTemplate(item.id)
    await loadCardTemplates()
  })
}

async function removeGroupBuy(item: AdminGroupBuy): Promise<void> {
  await confirmRemoveEntity('拼团活动', item.name, async () => {
    await deleteGroupBuy(item.id)
    await loadGroupBuys()
  })
}

async function removeBargain(item: AdminBargainActivity): Promise<void> {
  await confirmRemoveEntity('砍价活动', item.name, async () => {
    await deleteBargain(item.id)
    await loadBargains()
  })
}

async function removeInviteConfig(item: AdminInviteConfig): Promise<void> {
  await confirmRemoveEntity('邀请奖励配置', item.scene || item.id, async () => {
    await deleteInviteConfig(item.id)
    await loadInviteConfigs()
  })
}

async function removeFlashSale(item: AdminFlashSale): Promise<void> {
  await confirmRemoveEntity('秒杀活动', item.name, async () => {
    await deleteFlashSale(item.id)
    await loadFlashSales()
  })
}

async function submitCouponIssue(): Promise<void> {
  if (!couponIssueForm.couponId) return
  couponSubmitting.value = true
  try {
    const payload: CouponIssuePayload = {
      ...couponIssueForm,
      targetIds: couponIssueForm.targetType === 'USER'
        ? [...couponIssueTargetUserIds.value]
        : undefined,
    }
    await issueCoupon(payload)
    ElMessage.success('优惠券已发放')
    couponIssueVisible.value = false
    await loadCoupons()
  } catch (error) {
    ElMessage.error(error instanceof Error ? error.message : '优惠券发放失败')
  } finally {
    couponSubmitting.value = false
  }
}

async function openBannerEditor(item?: AdminBanner): Promise<void> {
  await ensureStoreOptions()
  Object.assign(bannerForm, {
    id: item?.id,
    title: item?.title || '',
    imageUrl: item?.imageUrl || '',
    linkType: item?.linkType || '',
    linkValue: item?.linkValue || '',
    storeId: item?.storeId || '',
    sort: item?.sort ?? 0,
    enabled: item?.enabled ?? true,
    startAt: item?.startAt || '',
    endAt: item?.endAt || '',
  })
  bannerStoreId.value = item?.storeId || (!userStore.isPlatformScope ? (userStore.visibleStoreIds[0] ?? '') : '')
  bannerEditorVisible.value = true
}

async function submitBanner(): Promise<void> {
  if (!bannerForm.title?.trim() || !bannerForm.imageUrl?.trim()) {
    ElMessage.warning('请填写 Banner 标题并选择 Banner 图片')
    return
  }
  if (!userStore.isPlatformScope && !bannerStoreId.value) {
    ElMessage.warning('请选择适用门店')
    return
  }
  bannerSubmitting.value = true
  try {
    const payload: BannerSavePayload = {
      ...bannerForm,
      title: bannerForm.title.trim(),
      imageUrl: bannerForm.imageUrl.trim(),
      linkType: bannerForm.linkType?.trim() || '',
      linkValue: bannerForm.linkValue?.trim() || '',
      storeId: bannerStoreId.value || undefined,
      startAt: bannerForm.startAt?.trim() || '',
      endAt: bannerForm.endAt?.trim() || '',
    }
    if (isEditingBanner.value) {
      await updateBanner(payload)
      ElMessage.success('首页 Banner 已更新')
    } else {
      delete payload.id
      if (!payload.linkType) delete payload.linkType
      if (!payload.linkValue) delete payload.linkValue
      if (!payload.startAt) delete payload.startAt
      if (!payload.endAt) delete payload.endAt
      await createBanner(payload)
      ElMessage.success('首页 Banner 已创建')
    }
    bannerEditorVisible.value = false
    await loadBanners()
  } catch (error) {
    ElMessage.error(error instanceof Error ? error.message : '首页 Banner 保存失败')
  } finally {
    bannerSubmitting.value = false
  }
}

async function openRechargePackageEditor(item?: AdminRechargePackage): Promise<void> {
  if (!couponSelectOptions.value.length) await loadCouponOptions()
  rechargePackageEditing.value = Boolean(item)
  Object.assign(rechargePackageForm, {
    id: item?.id,
    name: item?.name || '',
    amount: fenAmountToYuan(item?.amount) ?? 0,
    giftBalance: fenAmountToYuan(item?.giftBalance) ?? 0,
    giftCouponTemplateIds: item?.giftCouponTemplateIds || '',
    sort: item?.sort ?? 0,
    enabled: item?.status === 'ENABLED',
  })
  rechargePackageGiftCouponTemplateIds.value = parseDelimitedText(item?.giftCouponTemplateIds || '')
  rechargePackageEditorVisible.value = true
}

async function submitRechargePackage(): Promise<void> {
  if (!rechargePackageForm.name?.trim()) {
    ElMessage.warning('请填写套餐名称')
    return
  }
  rechargeSubmitting.value = true
  try {
    const payload: RechargePackageSavePayload = {
      ...rechargePackageForm,
      name: rechargePackageForm.name.trim(),
      giftCouponTemplateIds: rechargePackageGiftCouponTemplateIds.value.join(',') || undefined,
      amount: yuanAmountToFen(rechargePackageForm.amount),
      giftBalance: yuanAmountToFen(rechargePackageForm.giftBalance),
    }
    if (isEditingRechargePackage.value) {
      await updateRechargePackage(payload)
      ElMessage.success('充值套餐已更新')
    } else {
      delete payload.id
      await createRechargePackage(payload)
      ElMessage.success('充值套餐已创建')
    }
    rechargePackageEditorVisible.value = false
    await loadRechargePackages()
  } catch (error) {
    ElMessage.error(error instanceof Error ? error.message : '充值套餐保存失败')
  } finally {
    rechargeSubmitting.value = false
  }
}

async function openCardTemplateEditor(item?: AdminCardTemplate): Promise<void> {
  await ensureEditorOptions()
  Object.assign(cardTemplateForm, {
    id: item?.id,
    name: item?.name || '',
    category: item?.category || 'COUNT_CARD',
    salePrice: fenAmountToYuan(item?.salePrice) ?? 0,
    faceValue: fenAmountToYuan(item?.faceValue) ?? 0,
    validDays: item?.validDays ?? 30,
    countValue: item?.countValue ?? 0,
    benefit: item?.benefit || '',
    totalCount: item?.totalCount ?? 0,
    perUserLimit: item?.perUserLimit ?? 1,
    storeIds: item?.storeIds || '',
    status: item?.status || 'ENABLED',
  })
  cardTemplateStoreIds.value = parseDelimitedText(item?.storeIds || '')
  if (!item && !userStore.isPlatformScope && !cardTemplateStoreIds.value.length) {
    cardTemplateStoreIds.value = [...userStore.visibleStoreIds]
  }
  cardTemplateEditorVisible.value = true
}

async function openGroupBuyEditor(item?: AdminGroupBuy): Promise<void> {
  await ensureEditorOptions()
  Object.assign(groupBuyForm, {
    id: item?.id,
    name: item?.name || '',
    productId: item?.productId || '',
    groupPrice: fenAmountToYuan(item?.groupPrice) ?? 0,
    teamSize: item?.teamSize ?? 2,
    durationHours: item?.durationHours ?? 24,
    storeIds: item?.storeIds || '',
    status: item?.status || 'DRAFT',
    startAt: item?.startAt || '',
    endAt: item?.endAt || '',
  })
  groupBuyStoreIds.value = parseDelimitedText(item?.storeIds || '')
  if (!item && !userStore.isPlatformScope && !groupBuyStoreIds.value.length) {
    groupBuyStoreIds.value = [...userStore.visibleStoreIds]
  }
  groupBuyEditorVisible.value = true
}

async function openBargainEditor(item?: AdminBargainActivity): Promise<void> {
  await ensureEditorOptions()
  Object.assign(bargainForm, {
    id: item?.id,
    name: item?.name || '',
    productId: item?.productId || '',
    floorPrice: fenAmountToYuan(item?.floorPrice) ?? 0,
    initPrice: fenAmountToYuan(item?.initPrice) ?? 0,
    helpTimes: item?.helpTimes ?? 3,
    storeId: item?.storeId || '',
    status: item?.status || 'DRAFT',
  })
  bargainStoreIds.value = parseDelimitedText(item?.storeId || '')
  if (!item && !userStore.isPlatformScope && !bargainStoreIds.value.length) {
    bargainStoreIds.value = [...userStore.visibleStoreIds]
  }
  bargainEditorVisible.value = true
}

function openInviteEditor(item?: AdminInviteConfig): void {
  Object.assign(inviteForm, {
    id: item?.id,
    scene: item?.scene || 'REGISTER',
    inviterCouponTemplateId: item?.inviterCouponTemplateId || '',
    inviteeCouponTemplateId: item?.inviteeCouponTemplateId || '',
    maxTimes: item?.maxTimes ?? 1,
    enabled: item?.enabled ?? true,
  })
  inviteEditorVisible.value = true
}

async function submitCardTemplate(): Promise<void> {
  if (!cardTemplateForm.name?.trim()) {
    ElMessage.warning('请填写模板名称')
    return
  }
  if (!userStore.isPlatformScope && !cardTemplateStoreIds.value.length) {
    ElMessage.warning('请选择适用门店')
    return
  }
  cardSubmitting.value = true
  try {
    const payload: CardTemplateSavePayload = {
      ...cardTemplateForm,
      name: cardTemplateForm.name.trim(),
      benefit: cardTemplateForm.benefit?.trim() || undefined,
      storeIds: cardTemplateStoreIds.value.join(',') || undefined,
      salePrice: yuanAmountToFen(cardTemplateForm.salePrice),
      faceValue: yuanAmountToFen(cardTemplateForm.faceValue),
    }
    if (isEditingCardTemplate.value) {
      await updateCardTemplate(payload)
      ElMessage.success('卡券模板已更新')
    } else {
      delete payload.id
      await createCardTemplate(payload)
      ElMessage.success('卡券模板已创建')
    }
    cardTemplateEditorVisible.value = false
    await loadCardTemplates()
  } catch (error) {
    ElMessage.error(error instanceof Error ? error.message : '卡券模板保存失败')
  } finally {
    cardSubmitting.value = false
  }
}

async function submitGroupBuy(): Promise<void> {
  if (!groupBuyForm.name?.trim()) {
    ElMessage.warning('请填写活动名称')
    return
  }
  if (!userStore.isPlatformScope && !groupBuyStoreIds.value.length) {
    ElMessage.warning('请选择适用门店')
    return
  }
  groupBuySubmitting.value = true
  try {
    const payload: GroupBuySavePayload = {
      ...groupBuyForm,
      name: groupBuyForm.name.trim(),
      productId: groupBuyForm.productId?.trim() || undefined,
      storeIds: groupBuyStoreIds.value.length ? JSON.stringify(groupBuyStoreIds.value) : undefined,
      startAt: groupBuyForm.startAt?.trim() || undefined,
      endAt: groupBuyForm.endAt?.trim() || undefined,
      groupPrice: yuanAmountToFen(groupBuyForm.groupPrice),
    }
    if (isEditingGroupBuy.value) {
      await updateGroupBuy(payload)
      ElMessage.success('拼团活动已更新')
    } else {
      delete payload.id
      await createGroupBuy(payload)
      ElMessage.success('拼团活动已创建')
    }
    groupBuyEditorVisible.value = false
    await loadGroupBuys()
  } catch (error) {
    ElMessage.error(error instanceof Error ? error.message : '拼团活动保存失败')
  } finally {
    groupBuySubmitting.value = false
  }
}

async function submitBargain(): Promise<void> {
  if (!bargainForm.name?.trim()) {
    ElMessage.warning('请填写活动名称')
    return
  }
  if (!userStore.isPlatformScope && !bargainStoreIds.value.length) {
    ElMessage.warning('请选择适用门店')
    return
  }
  bargainSubmitting.value = true
  try {
    const payload: BargainSavePayload = {
      ...bargainForm,
      name: bargainForm.name.trim(),
      productId: bargainForm.productId?.trim() || undefined,
      storeId: bargainStoreIds.value.join(',') || undefined,
      floorPrice: yuanAmountToFen(bargainForm.floorPrice),
      initPrice: yuanAmountToFen(bargainForm.initPrice),
    }
    if (isEditingBargain.value) {
      await updateBargain(payload)
      ElMessage.success('砍价活动已更新')
    } else {
      delete payload.id
      await createBargain(payload)
      ElMessage.success('砍价活动已创建')
    }
    bargainEditorVisible.value = false
    await loadBargains()
  } catch (error) {
    ElMessage.error(error instanceof Error ? error.message : '砍价活动保存失败')
  } finally {
    bargainSubmitting.value = false
  }
}

async function submitInvite(): Promise<void> {
  inviteSubmitting.value = true
  try {
    const payload: InviteConfigSavePayload = {
      ...inviteForm,
      scene: inviteForm.scene?.trim() || 'REGISTER',
      inviterCouponTemplateId: inviteForm.inviterCouponTemplateId?.trim() || undefined,
      inviteeCouponTemplateId: inviteForm.inviteeCouponTemplateId?.trim() || undefined,
    }
    if (isEditingInvite.value) {
      await updateInviteConfig(payload)
      ElMessage.success('邀请奖励已更新')
    } else {
      delete payload.id
      await createInviteConfig(payload)
      ElMessage.success('邀请奖励已创建')
    }
    inviteEditorVisible.value = false
    await loadInviteConfigs()
  } catch (error) {
    ElMessage.error(error instanceof Error ? error.message : '邀请奖励保存失败')
  } finally {
    inviteSubmitting.value = false
  }
}

async function openFlashSaleEditor(item?: AdminFlashSale): Promise<void> {
  await ensureEditorOptions()
  Object.assign(flashSaleForm, {
    id: item?.id,
    name: item?.name || '',
    storeId: item?.storeId || '',
    startAt: item?.startAt || '',
    endAt: item?.endAt || '',
    status: item?.status || 'DRAFT',
    items: item?.items ? item.items.map((saleItem) => ({ ...saleItem })) : [],
  })
  flashSaleStoreIds.value = parseDelimitedText(item?.storeId || '')
  if (!item && !userStore.isPlatformScope && !flashSaleStoreIds.value.length) {
    flashSaleStoreIds.value = [...userStore.visibleStoreIds]
  }
  Object.assign(flashSaleItemForm, {
    productId: '',
    productName: '',
    skuId: '',
    salePrice: 0,
    limitQuantity: 1,
    stock: 0,
    sold: 0,
  })
  flashSaleEditorVisible.value = true
}

function appendFlashSaleItem(): void {
  if (!flashSaleItemForm.productId.trim()) {
    ElMessage.warning('请填写商品 ID')
    return
  }
  flashSaleForm.items = [
    ...(flashSaleForm.items ?? []),
    {
      productId: flashSaleItemForm.productId.trim(),
      productName: flashSaleItemForm.productName?.trim() || undefined,
      skuId: flashSaleItemForm.skuId?.trim() || undefined,
      salePrice: yuanAmountToFen(flashSaleItemForm.salePrice),
      limitQuantity: flashSaleItemForm.limitQuantity,
      stock: flashSaleItemForm.stock,
      sold: flashSaleItemForm.sold ?? 0,
    },
  ]
  Object.assign(flashSaleItemForm, {
    productId: '',
    productName: '',
    skuId: '',
    salePrice: 0,
    limitQuantity: 1,
    stock: 0,
    sold: 0,
  })
}

function removeFlashSaleItem(index: number): void {
  flashSaleForm.items = (flashSaleForm.items ?? []).filter((_, itemIndex) => itemIndex !== index)
}

async function submitFlashSale(): Promise<void> {
  if (!flashSaleForm.name?.trim()) {
    ElMessage.warning('请填写活动名称')
    return
  }
  if (!userStore.isPlatformScope && !flashSaleStoreIds.value.length) {
    ElMessage.warning('请选择适用门店')
    return
  }
  flashSubmitting.value = true
  try {
    const payload: FlashSaleSavePayload = {
      ...flashSaleForm,
      name: flashSaleForm.name.trim(),
      storeId: flashSaleStoreIds.value.join(',') || undefined,
      startAt: flashSaleForm.startAt?.trim() || undefined,
      endAt: flashSaleForm.endAt?.trim() || undefined,
      items: flashSaleForm.items ?? [],
    }
    if (isEditingFlashSale.value) {
      await updateFlashSale(payload)
      ElMessage.success('秒杀活动已更新')
    } else {
      delete payload.id
      await createFlashSale(payload)
      ElMessage.success('秒杀活动已创建')
    }
    flashSaleEditorVisible.value = false
    await loadFlashSales()
  } catch (error) {
    ElMessage.error(error instanceof Error ? error.message : '秒杀活动保存失败')
  } finally {
    flashSubmitting.value = false
  }
}

async function submitOrderStatus(): Promise<void> {
  if (!selectedOrder.value || !statusForm.status) return
  if (!getAllowedOrderStatusTransitions(selectedOrder.value.status).includes(statusForm.status)) {
    ElMessage.warning('当前订单不允许流转到该状态')
    return
  }
  statusSubmitting.value = true
  try {
    await updateOrderStatus(selectedOrder.value.id, { status: statusForm.status, remark: statusForm.remark || undefined })
    ElMessage.success('订单状态已更新')
    await openOrderDetail(selectedOrder.value.id)
    await loadOrders()
  } catch (error) {
    ElMessage.error(error instanceof Error ? error.message : '订单状态更新失败')
  } finally {
    statusSubmitting.value = false
  }
}

async function updateOrderStatusDirect(id: string, status: OrderStatus): Promise<void> {
  const order = orderRows.value.find((item) => item.id === id)
  if (order && !getAllowedOrderStatusTransitions(order.status).includes(status)) {
    ElMessage.warning('当前订单不允许流转到该状态')
    return
  }
  statusSubmitting.value = true
  try {
    await updateOrderStatus(id, { status })
    ElMessage.success('订单状态已更新')
    await loadOrders()
    if (selectedOrder.value?.id === id) await openOrderDetail(id)
  } catch (error) {
    ElMessage.error(error instanceof Error ? error.message : '订单状态更新失败')
  } finally {
    statusSubmitting.value = false
  }
}

async function submitRefundAudit(): Promise<void> {
  if (!selectedRefund.value || !canAuditRefund(selectedRefund.value.status)) return
  refundSubmitting.value = true
  try {
    await auditRefund(selectedRefund.value.id, { result: refundForm.result, reason: refundForm.reason || undefined })
    ElMessage.success('退款审核已提交')
    refundAuditVisible.value = false
    await loadRefunds()
  } catch (error) {
    ElMessage.error(error instanceof Error ? error.message : '退款审核失败')
  } finally {
    refundSubmitting.value = false
  }
}

async function refreshWechatStatus(refund: AdminRefund): Promise<void> {
  if (refreshingRefundId.value) return
  refreshingRefundId.value = refund.id
  try {
    const result = await refreshWechatRefundStatus(refund.id)
    ElMessage.success(result.message || '微信退款状态已刷新')
    await loadRefunds()
  } catch (error) {
    ElMessage.error(error instanceof Error ? error.message : '微信退款状态刷新失败')
  } finally {
    refreshingRefundId.value = ''
  }
}

/**
 * 该订单当前是否可取消配送费。
 *
 * 与后端 `OrderService.cancelDeliveryFee()` 的前置校验保持一致：自取订单没有配送费；
 * 配送费已为 0 时后端虽幂等返回 `ALREADY_ZERO`，但按钮已无意义，前端直接隐藏；
 * 已取消/已退款/退款中的订单后端会拒（此处提前隐藏，避免用户点了才报错）。
 *
 * 注意：已支付订单还需要 `order:refunds:edit` 权限，权限不足时后端返回 403，
 * 由调用处提示，前端不做本地权限判断（避免与后端的码/别名下发口径不一致）。
 */
function canCancelDeliveryFee(order: AdminOrder): boolean {
  if (order.deliveryType === DeliveryType.Pickup) return false
  if (!(order.deliveryFee > 0)) return false
  return !(
    order.status === OrderStatus.Cancelled ||
    order.status === OrderStatus.Refunded ||
    order.status === OrderStatus.Refunding
  )
}

/** 该退款单当前是否可重发微信退款：仅微信渠道且处于退款中（后端同条件 + 查微信实态）。 */
function canRetryWechatRefund(refund: AdminRefund): boolean {
  return refund.channel === 'WECHAT' && refund.status === RefundStatus.Refunding
}

/** 把取消配送费的返回结果转成面向运营的提示文案，按 `mode` 分支，不写死单一文案。 */
function describeDeliveryFeeCancelResult(result: DeliveryFeeCancelResult): string {
  const feeText = formatAmount(result.deliveryFeeBefore ?? 0)
  if (result.mode === 'ALREADY_ZERO') return '该订单配送费已为 0，无需重复操作'
  if (result.mode === 'UNPAID_DIRECT') {
    return `已减免配送费 ${feeText}，原微信预支付已关闭，用户需按新金额重新支付`
  }
  const refundNo = result.refundNo ? `（退款单 ${result.refundNo}）` : ''
  if (result.refundStatus === 'REFUNDED') return `已退款配送费 ${feeText}${refundNo}`
  if (result.refundStatus === 'WAITING_SHARE_RETURN') {
    return `退款处理中${refundNo}：等待分账回退，由后端任务续跑，请勿重复操作`
  }
  return `退款处理中${refundNo}：微信受理后到账，可稍后刷新退款状态`
}

/** 取消配送费前置确认（金额与后果差异较大，未支付/已支付分别提示）。 */
async function confirmCancelDeliveryFee(): Promise<void> {
  const order = selectedOrder.value
  if (!order || !canCancelDeliveryFee(order)) return
  const paid = order.paymentStatus === PaymentStatus.Paid
  try {
    await ElMessageBox.confirm(
      paid
        ? `确定取消订单 ${order.orderNo} 的配送费吗？将为已支付订单生成一笔仅含配送费（${formatAmount(order.deliveryFee)}）的部分退款，订单状态与优惠券不受影响。`
        : `确定取消订单 ${order.orderNo} 的配送费（${formatAmount(order.deliveryFee)}）吗？该订单未支付，会先关闭微信预支付再直接减免金额。`,
      '取消配送费',
      { type: 'warning', confirmButtonText: '确认取消配送费', cancelButtonText: '取消' },
    )
  } catch {
    return
  }
  await runCancelDeliveryFee(order.id, deliveryFeeReason.value)
}

/** 列表行直接触发：先确认，再执行。原因输入只在详情弹窗提供。 */
async function requestCancelDeliveryFee(id: string): Promise<void> {
  const order = orderRows.value.find((item) => item.id === id)
  if (!order || !canCancelDeliveryFee(order)) return
  try {
    await ElMessageBox.confirm(
      `确定取消订单 ${order.orderNo} 的配送费（${formatAmount(order.deliveryFee)}）吗？请在订单详情填写原因。`,
      '取消配送费',
      { type: 'warning', confirmButtonText: '确认取消配送费', cancelButtonText: '取消' },
    )
  } catch {
    return
  }
  await runCancelDeliveryFee(id, '')
}

async function runCancelDeliveryFee(id: string, reason: string): Promise<void> {
  deliveryFeeSubmitting.value = true
  try {
    const result = await cancelDeliveryFee(id, reason ? { reason } : {})
    ElMessage.success(describeDeliveryFeeCancelResult(result))
    deliveryFeeReason.value = ''
    await loadOrders()
    if (selectedOrder.value?.id === id) await openOrderDetail(id)
  } catch (error) {
    ElMessage.error(error instanceof Error ? error.message : '取消配送费失败')
  } finally {
    deliveryFeeSubmitting.value = false
  }
}

/** 重发微信退款：二次确认后调用，失败信息可直接展示给运维。 */
async function handleRetryWechatRefund(refund: AdminRefund): Promise<void> {
  if (retryingRefundId.value) return
  if (!canRetryWechatRefund(refund)) {
    ElMessage.warning('仅微信渠道且处于退款中的退款单可重发')
    return
  }
  try {
    await ElMessageBox.confirm(
      `确定重发退款单 ${refund.refundNo} 的微信退款吗？重发会生成新的商户退款单号，请先确认微信侧原退款已关闭或异常（可先点「刷新微信退款状态」查看实态）。`,
      '重发微信退款',
      { type: 'warning', confirmButtonText: '确认重发', cancelButtonText: '取消' },
    )
  } catch {
    return
  }
  await confirmRetryWechatRefund(refund)
}

/** 由弹窗内的「重发微信退款」按钮触发（此时已确认过，直接执行）。 */
async function confirmRetryWechatRefund(refund?: AdminRefund): Promise<void> {
  const target = refund ?? selectedRefund.value
  if (!target || retryingRefundId.value) return
  retryingRefundId.value = target.id
  try {
    await retryWechatRefund(target.id)
    ElMessage.success('微信退款已重发，请稍后刷新退款状态')
    await loadRefunds()
    if (refundAuditVisible.value) {
      const latest = refundRows.value.find((item) => item.id === target.id)
      if (latest) selectedRefund.value = latest
    }
  } catch (error) {
    ElMessage.error(error instanceof Error ? error.message : '微信退款重发失败')
  } finally {
    retryingRefundId.value = ''
  }
}

async function submitStoreSettings(): Promise<void> {
  if (!selectedStore.value) return
  storeSettingsSubmitting.value = true
  try {
    await updateStoreSettings(selectedStore.value.id, {
      openTime: storeSettingsForm.openTime,
      closeTime: storeSettingsForm.closeTime,
      suspendOrdering: storeSettingsForm.suspendOrdering,
      suspendReason: storeSettingsForm.suspendReason || undefined,
      pickupPrepareMinutes: storeSettingsForm.pickupPrepareMinutes,
      deliveryPrepareMinutes: storeSettingsForm.deliveryPrepareMinutes,
    })
    ElMessage.success('门店设置已更新')
    storeSettingsVisible.value = false
    await loadStores()
  } catch (error) {
    ElMessage.error(error instanceof Error ? error.message : '门店设置更新失败')
  } finally {
    storeSettingsSubmitting.value = false
  }
}

function parseDelimitedText(value: string): string[] {
  try {
    const parsed = JSON.parse(value)
    if (Array.isArray(parsed)) return parsed.map(String).map((item) => item.trim()).filter(Boolean)
  } catch {
    // 兼容历史逗号分隔值
  }
  return value
    .split(/[\n,，]/)
    .map((item) => item.trim())
    .filter(Boolean)
}
</script>

<style scoped lang="scss">
.operation-page { display: flex; flex-direction: column; gap: 16px; }
.operation-page.is-readonly :deep(.row-actions) { display: none; }
.operation-page.is-readonly :deep(.business-card__footer > div) { display: none; }
</style>
